import {
  AttemptEventTypeDto,
  AttemptModeDto,
  AttemptStatusDto,
  AttemptTimelineKindDto,
  BulkOperationDto,
  CheckFailureDto,
  CheckStatusDto,
  CheckStepNameDto,
  ConversionProblemCodeDto,
  HistoryEventKindDto,
  ImportErrorCodeDto,
  MediaRejectionReasonDto,
  MistakeReviewDto,
  PoolSkipReasonDto,
  QuestionPurposeDto,
  QuestionRuleDto,
  QuestionTypeDto,
  ScoringRuleDto,
  SourceTypeDto,
  SurveyGapCodeDto,
  SurveyResponseStatusDto,
  SurveyStatusDto,
  TestGapCodeDto,
  TestPartKindDto,
  TestPoolWarningCodeDto,
  TestPurposeDto,
  TestStatusDto,
  TranslationStatusDto,
  VersionStatusDto,
} from '@/api/generated';
import { PolishPlurals } from '@/assets/locales/PolishPlurals.model.ts';
import { ViewsTranslation } from '@/assets/locales/views/views.translation.en.ts';
import { QuestionSortKeyEnum } from '@/components/questionBankList/model/QuestionSortKey.enum.ts';
import { StatusActionEnum } from '@/models/status/StatusAction.enum.ts';
import { AttemptOutcomeEnum } from '@/views/attempt/model/AttemptOutcome.enum.ts';
import { AttemptDetailsTabEnum } from '@/views/attempts/model/AttemptDetailsTab.enum.ts';
import { AttemptResultFilterEnum } from '@/views/attempts/model/AttemptResultFilter.enum.ts';
import { GrantBlockEnum } from '@/views/attempts/model/GrantBlock.enum.ts';
import {
  DictionarySectionEnum,
  DictionaryStatusFilterEnum,
} from '@/views/dictionaryManagement/model/DictionaryManagement.enum.ts';
import { ImportDialogModeEnum } from '@/views/questionBank/model/ImportDialogMode.enum.ts';
import { NewVersionModeEnum } from '@/views/questionForm/model/NewVersionMode.enum.ts';
import { PositionScopeEnum } from '@/views/questionForm/model/PositionScope.enum.ts';
import { QuestionPageViewEnum } from '@/views/questionForm/model/QuestionPageView.enum.ts';
import { SurveyResponseStateEnum } from '@/views/surveys/model/SurveyResponseState.enum.ts';

export const viewsTranslation: PolishPlurals<ViewsTranslation> = {
  attempt: {
    ended: {
      description: 'Odpowiedzi zostały zapisane.',
      statusLabel: 'Test zakończony',
      title: 'Test się zakończył',
    },
    finish: {
      cancel: 'Wróć do testu',
      confirm: 'Zakończ test',
      description: 'Po zakończeniu nie można już zmienić odpowiedzi.',
      failed: 'Nie udało się zakończyć testu. Spróbuj ponownie.',
      open: 'Zakończ test',
      title: 'Zakończyć test?',
      unanswered_few:
        '{{count}} pytania nie mają jeszcze odpowiedzi. Odpowiedz na nie, aby zakończyć test.',
      unanswered_many:
        '{{count}} pytań nie ma jeszcze odpowiedzi. Odpowiedz na nie, aby zakończyć test.',
      unanswered_one:
        '{{count}} pytanie nie ma jeszcze odpowiedzi. Odpowiedz na nie, aby zakończyć test.',
      unansweredList: 'Pytania bez odpowiedzi: {{list}}.',
    },
    header: {
      answered: 'Odpowiedzi: {{answered}} z {{total}}',
      noTimeLimit: 'Bez limitu czasu',
      questionOf: 'Pytanie {{number}} z {{total}}',
      timeLeft: 'Pozostały czas',
      timeUp: 'Czas minął. Test się zamyka.',
    },
    instruction: 'Instrukcja',
    invalidated: {
      description:
        'To podejście zostało przerwane przez administratora. Odpowiedzi nie są już zapisywane. Zgłoś się do osoby, która uruchomiła test.',
      statusLabel: 'Podejście przerwane',
      title: 'Podejście zostało przerwane',
    },
    locked: {
      backToTests: 'Wróć do listy testów',
      description:
        'Wyniku nie można już otworzyć w tym oknie. Przekaż komputer osobie prowadzącej test.',
      statusLabel: 'Ekran zablokowany',
      title: 'Test jest zamknięty',
    },
    navigation: {
      answerRequired: 'Odpowiedz na pytanie, aby przejść dalej.',
      confirm: 'Zatwierdź i przejdź dalej',
      goTo: 'Przejdź do pytania {{number}}',
      label: 'Pytania',
      next: 'Następne',
      noGoingBack:
        'W tym teście nie można wrócić do wcześniejszych pytań. Zatwierdzonej odpowiedzi nie można zmienić.',
      previous: 'Poprzednie',
    },
    result: {
      close: 'Zakończ i zablokuj ekran',
      closeFailed: 'Nie udało się zablokować ekranu. Spróbuj ponownie.',
      expired: 'Czas na test minął. Wynik liczy odpowiedzi zapisane do tego momentu.',
      mistakes: {
        noAnswer: 'Bez odpowiedzi',
        questionNumber: 'Pytanie {{number}}',
        title: 'Błędy',
        withheld: 'Poprawnej odpowiedzi nie pokazujemy: to pytanie wróci w kolejnym podejściu.',
      },
      outcome: {
        [AttemptOutcomeEnum.FAILED]: { label: 'Niezaliczony', title: 'Test niezaliczony' },
        [AttemptOutcomeEnum.PASSED]: { label: 'Zaliczony', title: 'Test zaliczony' },
        [AttemptOutcomeEnum.SCORED]: { label: 'Wynik', title: 'Test zakończony' },
      },
      percent: '{{percent}}%',
      points: '{{points}} z {{maxPoints}} pkt',
      runner: {
        attemptsLeft_few: 'Pozostały {{count}} podejścia',
        attemptsLeft_many: 'Pozostało {{count}} podejść',
        attemptsLeft_one: 'Pozostało {{count}} podejście',
        retryAdvice: 'Zalecany odstęp przed kolejnym podejściem',
        title: 'Dla osoby prowadzącej test',
      },
    },
    saveFailed: 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.',
    unavailable: {
      description:
        'Test otwiera się tylko w przeglądarce, w której go uruchomiono. Wróć do tamtej przeglądarki albo poproś o pomoc osobę prowadzącą test.',
      statusLabel: 'Test niedostępny',
      title: 'Tego testu nie można tu otworzyć',
    },
  },
  attempts: {
    columns: {
      attemptNo: 'Podejście',
      duration: 'Czas',
      eventCount: 'Zdarzenia',
      person: 'Osoba',
      result: 'Wynik',
      startedAt: 'Rozpoczęto',
      status: 'Status',
      test: 'Test',
    },
    description: 'Podejścia do testów i ich wyniki.',
    details: {
      attemptNo: 'Podejście {{number}}',
      backToList: 'Wróć do podejść',
      continueExam: 'Wróć do egzaminu',
      fields: {
        allowGoingBack: 'Powrót do pytań',
        closedAt: 'Zamknięcie ekranu wyniku',
        duration: 'Czas rozwiązywania',
        eventCount: 'Zdarzenia',
        examiner: 'Egzaminator',
        finishedAt: 'Zakończono',
        finishedBy: 'Zakończono przez',
        passThreshold: 'Próg zaliczenia',
        personPosition: 'Stanowisko osoby',
        sapNumber: 'Numer SAP',
        startedAt: 'Rozpoczęto',
        startedBy: 'Uruchomiono przez',
        testPosition: 'Test dla stanowiska',
        timeLimit: 'Limit czasu',
      },
      forbidden: {
        action: 'Wróć do podejść',
        description: 'Jeśli potrzebujesz tego podejścia, poproś administratora o dostęp.',
        title: 'Nie masz dostępu do tego podejścia',
      },
      goingBack: {
        allowed: 'Dozwolony',
        notAllowed: 'Niedozwolony',
      },
      invalidate: {
        alreadyInvalidated: 'To podejście jest już unieważnione.',
        confirm: 'Unieważnij podejście',
        description:
          'Podejście nie będzie liczone do limitu ani do zaliczenia. Zostanie w historii razem z powodem.',
        descriptionRunning:
          'Podejście jeszcze trwa. Zakończy się od razu, a osoba zobaczy komunikat, że administrator je przerwał. Nie będzie liczone do limitu ani do zaliczenia.',
        failed: 'Nie udało się unieważnić podejścia. Spróbuj ponownie.',
        open: 'Unieważnij',
        success: 'Unieważniono podejście.',
        title: 'Unieważnić to podejście?',
      },
      invalidated: {
        byOnly: 'Przez: {{by}}.',
        reason: 'Powód: {{reason}}',
        title: 'Podejście unieważnione',
        unknownPerson: 'brak danych',
        when: 'Unieważniono {{date}}. Przez: {{by}}.',
      },
      notFound: {
        action: 'Wróć do podejść',
        description: 'Link mógł się zdezaktualizować. Znajdź podejście na liście.',
        title: 'Nie znaleziono tego podejścia',
      },
      noThreshold: 'Brak',
      noTimeLimit: 'Brak',
      notYet: 'Jeszcze nie',
      questions: {
        answeredAt: 'Odpowiedź {{time}}',
        empty: 'To podejście nie ma pytań.',
        exam: {
          answerKey: 'Klucz dla egzaminatora',
          comment: 'Komentarz egzaminatora',
          failed: 'Niezaliczone',
          grade: 'Ocena',
          gradedAt: 'Oceniono',
          gradedBy: 'Egzaminator',
          noComment: 'Bez komentarza',
          notGraded: 'Egzaminator nie ocenił jeszcze tego pytania.',
          passed: 'Zaliczone',
          points: '{{points}} z {{max}} pkt',
          score: '{{score}} z {{max}}',
          topicFallback: 'Zagadnienie {{number}}',
          topicNumber: '{{number}}.',
          topics: 'Zagadnienia, o które pytano',
        },
        noAnswer: 'Bez odpowiedzi',
        noGrade: 'Bez oceny',
        pointsPending: 'Jeszcze nieocenione',
        questionNumber: 'Pytanie {{number}}',
      },
      summaryLabel: 'Podsumowanie',
      tabs: {
        [AttemptDetailsTabEnum.QUESTIONS]: 'Pytania',
        [AttemptDetailsTabEnum.TIMELINE]: 'Oś czasu',
      },
      tabsLabel: 'Sekcje podejścia',
      threshold: '{{percent}}%',
      timeLimit: '{{count}} min',
      timeline: {
        by: 'Przez: {{name}}',
        empty: 'W tym podejściu nic jeszcze nie zapisano.',
        events: {
          [AttemptEventTypeDto.ContextMenu]: 'Otwarto menu kontekstowe',
          [AttemptEventTypeDto.Copy]: 'Skopiowano tekst',
          [AttemptEventTypeDto.FocusBack]: 'Wrócono do okna testu',
          [AttemptEventTypeDto.FocusLost]: 'Opuszczono okno testu',
          [AttemptEventTypeDto.FullscreenLeft]: 'Opuszczono pełny ekran',
          [AttemptEventTypeDto.Paste]: 'Wklejono tekst',
          [AttemptEventTypeDto.PrintScreen]: 'Naciśnięto klawisz Print Screen',
          [AttemptEventTypeDto.QuestionOpened]: 'Otwarto pytanie',
          [AttemptEventTypeDto.TabHidden]: 'Opuszczono kartę testu',
          [AttemptEventTypeDto.TabVisible]: 'Wrócono do karty testu',
        },
        forbidden: {
          description: 'Jeśli jej potrzebujesz, poproś administratora o dostęp.',
          title: 'Nie masz dostępu do tej osi czasu',
        },
        kinds: {
          [AttemptTimelineKindDto.AnswerChanged]: 'Zmieniono odpowiedź',
          [AttemptTimelineKindDto.Answered]: 'Udzielono odpowiedzi',
          [AttemptTimelineKindDto.Event]: 'Zdarzenie',
          [AttemptTimelineKindDto.Expired]: 'Czas minął',
          [AttemptTimelineKindDto.Finished]: 'Zakończono podejście',
          [AttemptTimelineKindDto.GradeChanged]: 'Zmieniono ocenę',
          [AttemptTimelineKindDto.Graded]: 'Wystawiono ocenę',
          [AttemptTimelineKindDto.Invalidated]: 'Unieważniono podejście',
          [AttemptTimelineKindDto.ResultClosed]: 'Zamknięto ekran wyniku',
          [AttemptTimelineKindDto.Started]: 'Rozpoczęto podejście',
        },
        label: 'Oś czasu podejścia',
        reason: 'Powód: {{reason}}',
      },
    },
    empty: {
      action: 'Przejdź do testów',
      description: 'Podejście pojawi się tutaj, gdy ktoś uruchomi test z listy testów.',
      title: 'Nie ma jeszcze podejść',
    },
    examMarker: 'Egzamin',
    filters: {
      allModes: 'Testy i egzaminy',
      allResults: 'Wszystkie wyniki',
      allStatuses: 'Wszystkie statusy',
      allTests: 'Wszystkie testy',
      mode: 'Rodzaj',
      result: 'Wynik',
      sapNumber: 'Numer SAP',
      sapNumberPlaceholder: 'Pełny numer SAP',
      startedFrom: 'Rozpoczęte od',
      startedTo: 'Rozpoczęte do',
      status: 'Status',
      test: 'Test',
    },
    mode: {
      [AttemptModeDto.Examined]: 'Egzaminy',
      [AttemptModeDto.Self]: 'Testy',
    },
    outcome: {
      [AttemptOutcomeEnum.FAILED]: 'Niezaliczony',
      [AttemptOutcomeEnum.PASSED]: 'Zaliczony',
      [AttemptOutcomeEnum.SCORED]: 'Oceniony',
    },
    percent: '{{percent}}%',
    person: {
      grant: {
        confirm: 'Przyznaj podejście',
        description:
          'Osoba będzie mogła rozpocząć test jeszcze raz, ponad limit. Powód zostanie zapisany w historii.',
        failed: 'Nie udało się przyznać podejścia. Spróbuj ponownie.',
        open: 'Przyznaj dodatkowe podejście',
        success: 'Przyznano dodatkowe podejście.',
        title: 'Przyznać dodatkowe podejście?',
        unavailable: {
          [GrantBlockEnum.LOCKED_AFTER_PASS]:
            'Ta osoba zaliczyła test, a po zaliczeniu nie można podejść do niego ponownie.',
          [GrantBlockEnum.NO_LIMIT]:
            'Ten test nie ma limitu podejść, więc nie ma czego przyznawać.',
        },
      },
      lock: {
        confirm: 'Zablokuj',
        description: 'Osoba nie rozpocznie tego testu, dopóki nie zdejmiesz blokady.',
        failed: 'Nie udało się zablokować podejść. Spróbuj ponownie.',
        open: 'Zablokuj',
        success: 'Zablokowano podejścia tej osoby.',
        title: 'Zablokować podejścia tej osoby?',
      },
      subject: 'Numer SAP {{sapNumber}} · {{test}}',
      subjectWithoutTest: 'Numer SAP {{sapNumber}}',
      title: 'Stan osoby w teście',
      unlock: {
        confirm: 'Odblokuj',
        description: 'Osoba znów będzie mogła rozpocząć ten test.',
        failed: 'Nie udało się odblokować podejść. Spróbuj ponownie.',
        open: 'Odblokuj',
        success: 'Odblokowano podejścia tej osoby.',
        title: 'Odblokować podejścia tej osoby?',
      },
    },
    points: '{{points}} z {{maxPoints}} pkt',
    result: {
      [AttemptResultFilterEnum.FAILED]: 'Niezaliczone',
      [AttemptResultFilterEnum.PASSED]: 'Zaliczone',
    },
    status: {
      [AttemptStatusDto.Expired]: 'Czas minął',
      [AttemptStatusDto.Finished]: 'Zakończone',
      [AttemptStatusDto.InProgress]: 'W trakcie',
      [AttemptStatusDto.Invalidated]: 'Unieważnione',
    },
    tableLabel: 'Podejścia do testów',
    title: 'Podejścia',
  },
  comingSoon: {
    description: 'Ten moduł jest w przygotowaniu. Pojawi się tutaj w jednej z kolejnych wersji.',
    goToDashboard: 'Przejdź do dashboardu',
    hint: 'Pozostałe moduły znajdziesz w menu po lewej stronie.',
    statusLabel: 'W przygotowaniu',
  },
  devPatterns: {
    description:
      'Ekran dla zespołu. Pokazuje wspólne komponenty na lokalnych danych przykładowych, bez połączenia z serwerem.',
    form: {
      description:
        'Walidacja działa najpierw w przeglądarce. Błędy zwrócone przez serwer pojawiają się przy odpowiednich polach.',
      fields: {
        category: 'Kategoria',
        content: 'Treść pytania',
        contentPlaceholder: 'Na przykład: Jak przechowywać towar chłodzony?',
        email: 'E-mail autora',
        emailPlaceholder: 'imie.nazwisko@firma.pl',
      },
      reset: 'Wyczyść formularz',
      savedMessage: 'Zapisano pytanie.',
      simulateServerError: 'Symuluj odrzucenie przez serwer',
      submit: 'Zapisz pytanie',
      title: 'Formularz',
    },
    notifications: {
      description: 'Krótkie potwierdzenia znikają same. Błędy zostają do czasu zamknięcia.',
      errorMessage: 'Nie udało się zapisać zmian. Spróbuj ponownie.',
      showError: 'Pokaż błąd',
      showSuccess: 'Pokaż potwierdzenie',
      successMessage: 'Zapisano zmiany.',
      title: 'Powiadomienia',
    },
    states: {
      calloutBody: 'Tego bloku używaj do informacji, które pomagają, ale nie blokują pracy.',
      calloutTitle: 'Informacja',
      description: 'Każda lista poza danymi ma trzy stany. Każdy ma własny komponent.',
      emptyLabel: 'Pusto',
      errorLabel: 'Błąd',
      loadingLabel: 'Wczytywanie',
      title: 'Stany ekranu',
    },
    table: {
      columns: {
        category: 'Kategoria',
        code: 'Kod',
        content: 'Treść',
        status: 'Status',
        updatedAt: 'Zmieniono',
      },
      description:
        'Stronicowanie, sortowanie i filtry trafiają do źródła danych jako jedno zapytanie i są zapisane w adresie, więc widokiem można się podzielić.',
      filters: {
        allCategories: 'Wszystkie kategorie',
        allStatuses: 'Wszystkie statusy',
        category: 'Kategoria',
        search: 'Szukaj pytań',
        status: 'Status',
      },
      showEmptyDataset: 'Pokaż pustą listę',
      title: 'Tabela',
    },
    title: 'Wzorce interfejsu',
  },
  dictionaries: {
    questionCategory: {
      COMMUNICATION: 'Komunikacja',
      CUSTOMER_SERVICE: 'Obsługa klienta',
      FOOD_SAFETY: 'Bezpieczeństwo żywności',
      OCCUPATIONAL_SAFETY: 'BHP',
    },
    questionPurpose: {
      [QuestionPurposeDto.Survey]: 'Ankieta',
      [QuestionPurposeDto.Test]: 'Test',
    },
    questionSource: {
      [SourceTypeDto.Handbook]: 'Podręcznik',
      [SourceTypeDto.Opl]: 'OPL',
      [SourceTypeDto.Other]: 'Inne',
      [SourceTypeDto.Procedure]: 'Procedura',
    },
    questionStatus: {
      ACTIVE: 'Aktywne',
      ARCHIVED: 'Zarchiwizowane',
      DRAFT: 'Szkic',
    },
    questionType: {
      [QuestionTypeDto.ExpectedAnswer]: 'Oczekiwana odpowiedź',
      [QuestionTypeDto.MultipleChoice]: 'Wielokrotny wybór',
      [QuestionTypeDto.OpenText]: 'Pytanie otwarte',
      [QuestionTypeDto.Ordering]: 'Ustalanie kolejności',
      [QuestionTypeDto.PassFail]: 'Zaliczone lub niezaliczone',
      [QuestionTypeDto.Practical]: 'Zadanie praktyczne',
      [QuestionTypeDto.SingleChoice]: 'Jednokrotny wybór',
    },
    scoringRule: {
      [ScoringRuleDto.AllOrNothing]: {
        description:
          'Punkty tylko za zaznaczenie wszystkich poprawnych odpowiedzi i żadnej błędnej.',
        label: 'Wszystko albo nic',
      },
      [ScoringRuleDto.Partial]: {
        description:
          'Punkty proporcjonalnie do zaznaczonych poprawnych odpowiedzi. Błędne zaznaczenia nic nie odejmują, więc zaznaczenie wszystkich daje komplet punktów.',
        label: 'Częściowa',
      },
      [ScoringRuleDto.PartialWithPenalty]: {
        description:
          'Każde błędne zaznaczenie odejmuje punkty. Wynik pytania nigdy nie spada poniżej zera.',
        label: 'Częściowa z karą',
      },
    },
    translationStatus: {
      [TranslationStatusDto.Approved]: 'Zatwierdzone',
      [TranslationStatusDto.Draft]: 'Szkic',
      [TranslationStatusDto.Missing]: 'Brak',
    },
    versionStatus: {
      [VersionStatusDto.Active]: 'Aktywna',
      [VersionStatusDto.Draft]: 'Szkic',
      [VersionStatusDto.Retired]: 'Wyłączona',
    },
  },
  dictionaryManagement: {
    categories: {
      actions: {
        activate: 'Włącz',
        deactivate: 'Wyłącz',
        delete: 'Usuń',
        deleteUnavailable: 'Kategoria ma pytania, więc nie można jej usunąć.',
        edit: 'Edytuj',
        menu: 'Akcje dla kategorii {{name}}',
        moveDown: 'Przesuń kategorię {{name}} niżej',
        moveUp: 'Przesuń kategorię {{name}} wyżej',
      },
      activated: 'Włączono kategorię {{name}}.',
      add: 'Dodaj kategorię',
      columns: {
        actions: 'Akcje',
        name: 'Nazwa',
        order: 'Kolejność',
        prefix: 'Prefiks',
        questions: 'Pytania',
        status: 'Status',
      },
      created: 'Dodano kategorię.',
      deactivated: 'Wyłączono kategorię {{name}}.',
      deleteDialog: {
        cancel: 'Anuluj',
        confirm: 'Usuń',
        description: 'Kategoria zniknie ze słownika. Tej operacji nie można cofnąć.',
        done: 'Usunięto kategorię {{name}}.',
        failed: 'Nie udało się usunąć kategorii. Spróbuj ponownie.',
        inUse: 'Kategoria jest używana. Wyłącz ją zamiast usuwać.',
        title: 'Usunąć kategorię {{name}}?',
      },
      empty: {
        description: 'Dodaj pierwszą kategorię, żeby autorzy mogli ją wybrać w pytaniu.',
        title: 'Nie ma jeszcze kategorii',
      },
      form: {
        cancel: 'Anuluj',
        createTitle: 'Nowa kategoria',
        editTitle: 'Edytuj kategorię',
        name: 'Nazwa',
        prefix: 'Prefiks',
        prefixHint: 'Na przykład BHP. Po dodaniu pierwszego pytania nie będzie można go zmienić.',
        prefixLocked_few: 'Prefiksu nie można zmienić, bo kategoria ma już {{count}} pytania.',
        prefixLocked_many: 'Prefiksu nie można zmienić, bo kategoria ma już {{count}} pytań.',
        prefixLocked_one: 'Prefiksu nie można zmienić, bo kategoria ma już {{count}} pytanie.',
        save: 'Zapisz',
      },
      orderHint: 'W tej kolejności autorzy widzą kategorie w formularzu pytania.',
      prefixNotice:
        'Z prefiksu powstają klucze pytań, na przykład BHP-1. Gdy kategoria ma już pytania, prefiksu nie można zmienić.',
      reorderFailed: 'Nie udało się zmienić kolejności. Spróbuj ponownie.',
      saved: 'Zapisano zmiany.',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Aktywna',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Wyłączona',
      },
      statusDialog: {
        cancel: 'Anuluj',
        confirm: 'Wyłącz',
        title: 'Wyłączyć kategorię {{name}}?',
        used_few:
          'Korzystają z niej {{count}} pytania. Zachowają ją, ale nowym pytaniom nie będzie można jej przypisać. Możesz ją później włączyć.',
        used_many:
          'Korzysta z niej {{count}} pytań. Zachowają ją, ale nowym pytaniom nie będzie można jej przypisać. Możesz ją później włączyć.',
        used_one:
          'Korzysta z niej {{count}} pytanie. Zachowa ją, ale nowym pytaniom nie będzie można jej przypisać. Możesz ją później włączyć.',
      },
      statusFailed: 'Nie udało się zmienić statusu kategorii. Spróbuj ponownie.',
      tableLabel: 'Kategorie pytań',
    },
    description: 'Kategorie, tagi, nazwy procedur i stanowiska, którymi autorzy opisują pytania.',
    positions: {
      columns: {
        code: 'Numer',
        company: 'Spółka',
        inAnkieter: 'Używane w Ankieterze',
        name: 'Stanowisko',
        questions: 'Pytania',
        status: 'Status',
      },
      empty: {
        description: 'Stanowiska pojawią się tutaj po pierwszym nocnym odświeżeniu z jAIn.',
        title: 'Nie ma jeszcze stanowisk',
      },
      noMatches: {
        description: 'Zmień wyszukiwany tekst albo filtry, żeby zobaczyć więcej stanowisk.',
        title: 'Żadne stanowisko nie pasuje',
      },
      notice:
        'Stanowiska pochodzą z jAIn i odświeżają się co noc. Tutaj możesz tylko wskazać, których używa Ankieter. Na listach wyboru są tylko aktywne stanowiska używane w Ankieterze. Pytania i testy zachowują stanowiska, które już mają. Ostatnie odświeżenie: {{date}}.',
      noticeNeverRefreshed:
        'Stanowiska pochodzą z jAIn i odświeżają się co noc. Tutaj możesz tylko wskazać, których używa Ankieter. Na listach wyboru są tylko aktywne stanowiska używane w Ankieterze. Pytania i testy zachowują stanowiska, które już mają. Słownik nie był jeszcze odświeżony.',
      search: 'Szukaj po nazwie albo numerze',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Aktywne',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Wyłączone',
      },
      statusFilter: {
        all: 'Wszystkie',
        label: 'Status',
        options: {
          [DictionaryStatusFilterEnum.ACTIVE]: 'Aktywne',
          [DictionaryStatusFilterEnum.INACTIVE]: 'Wyłączone',
        },
      },
      tableLabel: 'Stanowiska',
      usageFailed: 'Nie udało się zapisać zmiany dla stanowiska {{label}}. Spróbuj ponownie.',
      usageToggle: 'Używane w Ankieterze: {{label}}',
      usedOnly: 'Tylko używane w Ankieterze',
    },
    procedureNames: {
      columns: {
        name: 'Nazwa procedury',
      },
      empty: {
        description: 'Nazwa pojawi się tutaj, gdy autor wpisze ją jako źródło pytania.',
        title: 'Nie ma jeszcze nazw procedur',
      },
      notice:
        'Nazwy procedur wpisane w pytaniach, najczęściej używane na górze. Formularz pytania podpowiada je przy wpisywaniu, żeby ta sama procedura zawsze miała tę samą nazwę. Kliknij nazwę, aby zobaczyć pytania, które ją cytują.',
      search: 'Szukaj nazwy procedury',
      tableLabel: 'Nazwy procedur',
    },
    questionCount_few: '{{count}} pytania',
    questionCount_many: '{{count}} pytań',
    questionCount_one: '{{count}} pytanie',
    sections: {
      [DictionarySectionEnum.CATEGORIES]: 'Kategorie',
      [DictionarySectionEnum.POSITIONS]: 'Stanowiska',
      [DictionarySectionEnum.PROCEDURE_NAMES]: 'Nazwy procedur',
      [DictionarySectionEnum.TAGS]: 'Tagi',
    },
    sectionsLabel: 'Słownik',
    tags: {
      actions: {
        activate: 'Włącz',
        deactivate: 'Wyłącz',
        delete: 'Usuń',
        deleteUnavailable:
          'Tag ma pytania, więc nie można go usunąć. Wyłącz go albo scal z innym tagiem.',
        menu: 'Akcje dla tagu {{label}}',
        merge: 'Scal z innym tagiem',
        mergeUnavailable: 'Wyłączonego tagu nie można scalić',
      },
      activated: 'Włączono tag „{{label}}”.',
      add: 'Dodaj tag',
      columns: {
        actions: 'Akcje',
        label: 'Tag',
        questions: 'Pytania',
        status: 'Status',
      },
      created: 'Dodano tag.',
      deactivated: 'Wyłączono tag „{{label}}”.',
      deleteDialog: {
        cancel: 'Anuluj',
        confirm: 'Usuń',
        description: 'Tag zniknie ze słownika. Tej operacji nie można cofnąć.',
        done: 'Usunięto tag „{{label}}”.',
        failed: 'Nie udało się usunąć tagu. Spróbuj ponownie.',
        inUse: 'Tag jest używany. Wyłącz go albo scal z innym tagiem.',
        title: 'Usunąć tag „{{label}}”?',
      },
      empty: {
        description: 'Autorzy dodają tagi przy pisaniu pytań. Tag możesz też dodać tutaj.',
        title: 'Nie ma jeszcze tagów',
      },
      form: {
        cancel: 'Anuluj',
        label: 'Nazwa tagu',
        save: 'Dodaj',
        similar: 'Podobne tagi, które już istnieją',
        title: 'Nowy tag',
      },
      merge: {
        cancel: 'Anuluj',
        confirm: 'Scal tagi',
        noOptions: 'Brak pasujących tagów',
        preview_few:
          '{{count}} pytania przejdą z tagu „{{source}}” na „{{target}}”. Tag „{{source}}” zostanie wyłączony.',
        preview_many:
          '{{count}} pytań przejdzie z tagu „{{source}}” na „{{target}}”. Tag „{{source}}” zostanie wyłączony.',
        preview_one:
          '{{count}} pytanie przejdzie z tagu „{{source}}” na „{{target}}”. Tag „{{source}}” zostanie wyłączony.',
        source: 'Tag scalany',
        target: 'Tag docelowy',
        targetPlaceholder: 'Wybierz tag, który zostaje',
        title: 'Scal tag „{{label}}”',
        unavailable: 'Nie udało się wczytać tagów. Zamknij okno i spróbuj ponownie.',
      },
      merged: 'Scalono tag „{{source}}” z tagiem „{{target}}”.',
      mergeFailed: 'Nie udało się scalić tagów. Spróbuj ponownie.',
      notice:
        'Tagi, które znaczą to samo, na przykład „bhp” i „b.h.p.”, scal w jeden. Pytania przejdą na wybrany tag, a scalony tag zostanie wyłączony.',
      search: 'Szukaj tagu',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Aktywny',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Wyłączony',
      },
      statusDialog: {
        cancel: 'Anuluj',
        confirm: 'Wyłącz',
        title: 'Wyłączyć tag „{{label}}”?',
        unused:
          'Tag nie ma pytań. Po wyłączeniu nie będzie można go nadać nowym pytaniom. Możesz go później włączyć.',
        used_few:
          'Tag ma {{count}} pytania. Zostanie przy nich, ale nowym pytaniom nie będzie można go nadać. Możesz go później włączyć.',
        used_many:
          'Tag ma {{count}} pytań. Zostanie przy nich, ale nowym pytaniom nie będzie można go nadać. Możesz go później włączyć.',
        used_one:
          'Tag ma {{count}} pytanie. Zostanie przy nim, ale nowym pytaniom nie będzie można go nadać. Możesz go później włączyć.',
      },
      statusFailed: 'Nie udało się zmienić statusu tagu. Spróbuj ponownie.',
      statusFilter: {
        all: 'Wszystkie statusy',
        label: 'Status',
        options: {
          [DictionaryStatusFilterEnum.ACTIVE]: 'Aktywne',
          [DictionaryStatusFilterEnum.INACTIVE]: 'Wyłączone',
        },
      },
      tableLabel: 'Tagi pytań',
    },
    title: 'Słowniki',
  },
  exam: {
    attemptNo: 'Podejście {{number}}',
    backToList: 'Wróć do podejść',
    closing: {
      progress: 'Ocenione pytania: {{graded}} z {{total}}',
      title: 'Gdy każde pytanie ma ocenę, zakończ egzamin.',
    },
    details: 'Szczegóły podejścia',
    empty: 'Ten egzamin nie ma pytań.',
    finish: {
      action: 'Zakończ egzamin',
      cancel: 'Wróć do karty',
      confirm: 'Zakończ egzamin',
      description:
        'Po zakończeniu egzaminu nie można zmienić ocen. Wynik policzy się z wystawionych ocen.',
      failed: 'Nie udało się zakończyć egzaminu. Spróbuj ponownie.',
      gapDetails: ' · {{category}}: {{reason}}',
      gapQuestion: 'Pytanie {{number}}',
      gapReasons: {
        comment: 'brak wymaganego komentarza',
        grade: 'brak oceny',
        unsaved: 'ocena nie jest jeszcze zapisana',
      },
      gapsDescription: 'Oceń te pytania i dodaj wymagane komentarze, a potem zakończ egzamin.',
      gapsTitle: 'Nie wszystkie pytania mają ocenę',
      refusedTitle: 'Nie można jeszcze zakończyć egzaminu',
      success: 'Zakończono egzamin.',
      title: 'Zakończyć egzamin?',
    },
    finished: {
      description: 'Ocen nie można już zmienić. Wynik jest w podsumowaniu.',
      title: 'Egzamin jest zakończony',
    },
    forbidden: {
      action: 'Wróć do podejść',
      description: 'Jeśli masz prowadzić ten egzamin, poproś administratora o dostęp.',
      title: 'Nie masz dostępu do tego egzaminu',
    },
    grade: {
      commentMissing: 'Dodaj wymagany komentarz, zanim zakończysz egzamin.',
      failed: 'Nie udało się zapisać oceny pytania {{number}}. Spróbuj ponownie.',
      failedInline: 'Nie udało się zapisać oceny.',
      gaps: {
        passed: 'Wybierz zaliczone albo niezaliczone, aby zapisać ocenę.',
        points: 'Wpisz punkty z podanego zakresu, aby zapisać ocenę.',
        score: 'Wybierz ocenę w skali, aby ją zapisać.',
        topics_few: 'Zaznacz dokładnie {{count}} zagadnienia, aby zapisać ocenę.',
        topics_many: 'Zaznacz dokładnie {{count}} zagadnień, aby zapisać ocenę.',
        topics_one: 'Zaznacz dokładnie {{count}} zagadnienie, aby zapisać ocenę.',
      },
      over: 'Egzamin jest już zakończony albo unieważniony, więc oceny nie można zmienić.',
      retry: 'Spróbuj ponownie',
      saved: 'Ocena zapisana',
      saving: 'Zapisywanie oceny',
    },
    invalidated: {
      description: 'Ocen nie można zmienić, a wynik się nie liczy.',
      title: 'Egzamin został unieważniony',
    },
    notFound: {
      action: 'Wróć do podejść',
      description: 'Link mógł się zdezaktualizować. Znajdź egzamin na liście podejść.',
      title: 'Nie znaleziono tego egzaminu',
    },
    question: {
      notGraded: 'Bez oceny',
      number: 'Pytanie {{number}}',
      points: '{{points}} z {{maxPoints}} pkt',
    },
    summary: {
      finishedAt: 'Zakończono',
      finishedBy: 'Zakończono przez',
      label: 'Egzamin',
      noThreshold: 'Brak',
      notYet: 'Jeszcze nie',
      passThreshold: 'Próg zaliczenia',
      personPosition: 'Stanowisko osoby',
      progress: 'Ocenione pytania',
      progressValue: '{{graded}} z {{total}}',
      sapNumber: 'Numer SAP',
      startedAt: 'Rozpoczęto',
      startedBy: 'Egzaminator',
      testPosition: 'Egzamin dla stanowiska',
      threshold: '{{percent}}%',
    },
  },
  questionBank: {
    actions: {
      duplicate: 'Duplikuj',
      edit: 'Edytuj',
      history: 'Historia wersji',
      menu: 'Akcje dla pytania {{key}}',
      preview: 'Podgląd jak u pracownika',
      retire: 'Wyłącz',
      retireNoActiveVersion: 'Pytanie nie ma wersji w użyciu.',
    },
    addQuestion: 'Dodaj pytanie',
    bulk: {
      back: 'Wstecz',
      cancel: 'Anuluj',
      change: {
        addTags: 'Dodane tagi: {{tags}}. Pytania zachowają obecną wersję.',
        clearReview: 'Oznaczenie do przeglądu zostanie zdjęte.',
        markForReview: 'Pytania zostaną oznaczone do przeglądu.',
        markForReviewWithReason: 'Pytania zostaną oznaczone do przeglądu. Powód: {{reason}}',
        removeTags: 'Usunięte tagi: {{tags}}. Pytania zachowają obecną wersję.',
        retire:
          'Wersje w użyciu zostaną wyłączone, a pytania przestaną pojawiać się w testach. Wyłączone wersje zostają w historii.',
        setCategory: 'Nowa kategoria: {{category}}. Pytania zachowają obecną wersję.',
      },
      clear: 'Wyczyść zaznaczenie',
      done_few: 'Zmieniono {{count}} pytania.',
      done_many: 'Zmieniono {{count}} pytań.',
      done_one: 'Zmieniono {{count}} pytanie.',
      errors: {
        applyFailed: 'Nie udało się wprowadzić zmiany. Spróbuj ponownie.',
        changedSincePreview:
          'Pytania zmieniły się od podglądu. Sprawdź nowe liczby i potwierdź ponownie.',
      },
      next: 'Dalej',
      operations: {
        [BulkOperationDto.AddTags]: 'Dodaj tag',
        [BulkOperationDto.ClearReview]: 'Zdejmij oznaczenie',
        [BulkOperationDto.MarkForReview]: 'Oznacz do przeglądu',
        [BulkOperationDto.RemoveTags]: 'Zdejmij tag',
        [BulkOperationDto.Retire]: 'Wyłącz',
        [BulkOperationDto.SetCategory]: 'Zmień kategorię',
      },
      params: {
        category: 'Kategoria',
        categoryPlaceholder: 'Wybierz kategorię',
        noTags: 'Brak pasujących tagów',
        reason: 'Powód (opcjonalnie)',
        reasonPlaceholder: 'Na co zwrócić uwagę przy przeglądzie',
        tags: 'Tagi',
      },
      preview: {
        affected_few: 'Zmienią się {{count}} pytania',
        affected_many: 'Zmieni się {{count}} pytań',
        affected_one: 'Zmieni się {{count}} pytanie',
        large: {
          description_few:
            'Ta operacja zmienia {{count}} pytania naraz. Upewnij się, że zaznaczenie jest właściwe.',
          description_many:
            'Ta operacja zmienia {{count}} pytań naraz. Upewnij się, że zaznaczenie jest właściwe.',
          description_one:
            'Ta operacja zmienia {{count}} pytanie naraz. Upewnij się, że zaznaczenie jest właściwe.',
          label: 'Wpisz {{count}}, aby potwierdzić',
          title: 'Duża zmiana',
        },
        more: 'i {{count}} więcej',
        nothingToChange: 'Nic się nie zmieni. Zaznaczone pytania już są w tym stanie.',
        sample: 'Przykładowe pytania',
        skipped_few: '{{count}} pytania pozostaną bez zmian.',
        skipped_many: '{{count}} pytań pozostanie bez zmian.',
        skipped_one: '{{count}} pytanie pozostanie bez zmian.',
      },
      selectAllMatching: 'Zaznacz wszystkie pasujące do filtrów ({{count}})',
      selected_few: 'Zaznaczono {{count}} pytania',
      selected_many: 'Zaznaczono {{count}} pytań',
      selected_one: 'Zaznaczono {{count}} pytanie',
      selectedAllMatching_few: 'Zaznaczono wszystkie {{count}} pytania pasujące do filtrów',
      selectedAllMatching_many: 'Zaznaczono wszystkie {{count}} pytań pasujących do filtrów',
      selectedAllMatching_one: 'Zaznaczono {{count}} pytanie pasujące do filtrów',
      selectPage: 'Zaznacz pytania na tej stronie',
      selectRow: 'Zaznacz pytanie {{key}}',
    },
    columns: {
      actions: 'Akcje',
      category: 'Kategoria',
      key: 'Klucz',
      languages: 'Języki',
      positions: 'Stanowiska',
      status: 'Status',
      summary: 'Treść',
      type: 'Typ',
      updatedAt: 'Zmieniono',
      version: 'Wersja',
    },
    description:
      'Wszystkie pytania w jednym miejscu. Zawęź listę filtrami albo wyszukaj po treści.',
    draftInProgress: 'szkic wersji {{number}} w toku',
    empty: {
      description: 'Dodaj pierwsze pytanie, a pojawi się na tej liście.',
      title: 'Baza pytań jest pusta',
    },
    exportAction: {
      empty: 'Żadne pytanie nie pasuje do filtrów.',
      failed: 'Nie udało się pobrać pliku. Spróbuj ponownie.',
      hint_few: 'Pobierz {{count}} pytania pasujące do filtrów',
      hint_many: 'Pobierz {{count}} pytań pasujących do filtrów',
      hint_one: 'Pobierz {{count}} pytanie pasujące do filtrów',
      label: 'Eksportuj do Excela',
    },
    filters: {
      activeSummary: 'Zawężono do',
      all: 'Wszystkie',
      author: 'Autor',
      category: 'Kategoria',
      changedFrom: 'Zmienione od',
      changedTo: 'Zmienione do',
      clearAll: 'Wyczyść filtry',
      hide: 'Ukryj filtry',
      noPositions: 'Brak pasujących stanowisk',
      noProcedures: 'Brak pasujących procedur',
      noTags: 'Brak pasujących tagów',
      optionsUnavailable: 'Nie udało się wczytać części opcji filtrów. Odśwież stronę.',
      positionCode: 'Stanowisko',
      positionPlaceholder: 'Nazwa albo numer',
      positionScope: 'Przeznaczone dla',
      positionScopeAll: 'Wszystkich stanowisk',
      positionScopeSelected: 'Wybranych stanowisk',
      procedureName: 'Procedura',
      procedurePlaceholder: 'Nazwa procedury',
      purpose: 'Przeznaczenie',
      review: 'Przegląd',
      reviewMarked: 'Do przeglądu',
      reviewUnmarked: 'Bez oznaczenia',
      search: 'Treść lub klucz',
      show: 'Filtry',
      showWithCount: 'Filtry ({{count}})',
      source: 'Źródło',
      status: 'Status',
      tags: 'Tagi',
      translationStatus: 'Tłumaczenie',
      type: 'Typ',
    },
    hasMedia: 'Pytanie z materiałem',
    importAction: 'Importuj z Excela',
    importDialog: {
      ambiguousPositionCodes:
        'Tę nazwę ma kilka stanowisk: {{positions}}. Wpisz jeden z tych numerów zamiast nazwy.',
      cancel: 'Anuluj',
      checking: 'Sprawdzanie pliku {{name}}',
      chooseAnother: 'Wgraj inny plik',
      close: 'Zamknij',
      confirm_few: 'Importuj {{count}} pytania',
      confirm_many: 'Importuj {{count}} pytań',
      confirm_one: 'Importuj {{count}} pytanie',
      conversion: {
        categoriesUnavailable: 'Nie udało się wczytać kategorii. Odśwież stronę.',
        category: 'Kategoria',
        categoryPlaceholder: 'Wybierz kategorię',
        check: 'Sprawdź',
        checking: 'Sprawdzanie pliku {{name}}',
        description:
          'Zamień pulę pytań w dotychczasowym układzie na szablon importu. Wszystkie pytania trafią do wybranej kategorii. Nic nie zapisze się w bazie pytań.',
        dropzone: 'Przeciągnij plik z pulą tutaj albo kliknij, aby go wybrać',
        fileChosen: 'Wybrany plik: {{name}}',
        issues: {
          category: 'Ta kategoria nie jest już w użyciu. Wybierz inną.',
          checkFailed: 'Nie udało się sprawdzić pliku. Spróbuj ponownie.',
          convertFailed: 'Nie udało się przygotować pliku w szablonie. Spróbuj ponownie.',
          unreadable:
            'Nie udało się odczytać pliku. Pierwszy arkusz musi mieć kolumny NR i Pytanie.',
        },
        limits: 'Plik .xlsx, najwyżej {{maxSize}} MB i {{maxRows}} pytań',
        notes:
          'Po konwersji każde pytanie jest pytaniem testowym za 1 punkt i zaimportuje się jako szkic. Możesz to zmienić w pliku przed importem.',
        report: {
          columns: {
            number: 'Nr w puli',
            problem: 'Do uzupełnienia',
            row: 'Wiersz',
          },
          download: 'Pobierz w szablonie',
          downloadAgain: 'Pobierz ponownie',
          empty: 'Plik nie zawiera pytań. Sprawdź, czy pierwszy arkusz ma kolumny NR i Pytanie.',
          goToImport: 'Przejdź do importu',
          listLabel: 'Co uzupełnić',
          nextDescription:
            'Uzupełnij pobrany plik i wgraj go w zakładce Import z szablonu. Kolumny nr i uwagi import pomija.',
          nextTitle: 'Plik pobrany',
          noNumber: 'Brak',
          problemCodes: {
            [ConversionProblemCodeDto.NoCorrectAnswer]:
              'Żadna odpowiedź nie ma zielonego tła, więc nie wiadomo, która jest poprawna. Wskaż ją w pliku.',
            [ConversionProblemCodeDto.NoPosition]:
              'Pytanie nie ma zaznaczonego stanowiska. Wpisz stanowiska w pliku.',
            [ConversionProblemCodeDto.NoSource]: 'Brak źródła. Wpisz je w pliku.',
            [ConversionProblemCodeDto.ProcedureWithoutName]:
              'Źródłem jest procedura, ale bez nazwy. Wpisz nazwę procedury w pliku.',
            [ConversionProblemCodeDto.TooFewAnswers]:
              'Pytanie ma mniej niż dwie odpowiedzi. Dopisz brakujące odpowiedzi w pliku.',
            [ConversionProblemCodeDto.UnknownPosition]:
              'Ta kolumna stanowiska nie pasuje do żadnego aktywnego stanowiska. Wpisz stanowisko w pliku.',
            [ConversionProblemCodeDto.UnknownSource]:
              'Takiego źródła nie ma na liście. Wybierz źródło z listy w pliku.',
            [ConversionProblemCodeDto.UnreadableRow]:
              'Wiersz nie jest ani pytaniem z numerem w kolumnie NR, ani odpowiedzią z literą. Został pominięty; sprawdź go w pliku z pulą.',
          },
          problems: 'Do uzupełnienia',
          problemsDescription:
            'Pobierz plik w szablonie i uzupełnij wskazane miejsca. Ta sama lista jest w arkuszu <strong>Do poprawienia</strong>.',
          problemsTitle_few: 'Do uzupełnienia są {{count}} miejsca',
          problemsTitle_many: 'Do uzupełnienia jest {{count}} miejsc',
          problemsTitle_one: 'Do uzupełnienia jest {{count}} miejsce',
          questions: 'Pytania w pliku',
          readyDescription: 'Pobierz plik w szablonie i wgraj go w zakładce Import z szablonu.',
          readyTitle: 'Nie ma nic do uzupełnienia',
        },
      },
      done_few: 'Zaimportowano {{count}} pytania.',
      done_many: 'Zaimportowano {{count}} pytań.',
      done_one: 'Zaimportowano {{count}} pytanie.',
      errorCodes: {
        [ImportErrorCodeDto.AmbiguousPosition]:
          'Tę nazwę ma kilka stanowisk. Wpisz numer stanowiska zamiast nazwy.',
        [ImportErrorCodeDto.InvalidValue]:
          'Ta kolumna nie przyjmuje takiej wartości. Sprawdź opis kolumny w szablonie.',
        [ImportErrorCodeDto.KeyRepeated]: 'Ten sam klucz jest już we wcześniejszym wierszu pliku.',
        [ImportErrorCodeDto.KeyTaken]:
          'Pytanie z tym kluczem jest już w bazie. Zmień klucz albo zostaw pole puste.',
        [ImportErrorCodeDto.Required]: 'Pole jest puste, a pytanie go wymaga.',
        [ImportErrorCodeDto.Rule]: 'Pytanie nie spełnia zasad formularza pytania.',
        [ImportErrorCodeDto.TooLong]: 'Tekst jest za długi. Skróć go.',
        [ImportErrorCodeDto.UnknownCategory]: 'Nie ma kategorii w użyciu o tej nazwie.',
        [ImportErrorCodeDto.UnknownKey]:
          'Klucz w arkuszu <strong>Źródła</strong> nie pasuje do żadnego pytania w arkuszu <strong>Pytania</strong>. Usuń ten wiersz albo popraw klucz w jednym z arkuszy.',
        [ImportErrorCodeDto.UnknownPosition]:
          'Nie ma aktywnego stanowiska o tym numerze albo nazwie. Sprawdź arkusz <strong>Stanowiska</strong> w szablonie.',
      },
      issues: {
        changed: 'Baza pytań zmieniła się od sprawdzenia pliku. Plik jest sprawdzany ponownie.',
        checkFailed: 'Nie udało się sprawdzić pliku. Spróbuj ponownie.',
        forbidden: 'Nie masz uprawnień do importu pytań.',
        importFailed: 'Nie udało się zaimportować pytań. Spróbuj ponownie.',
        refused: 'Plik zawiera błędy. Popraw je według raportu i wgraj plik ponownie.',
        tooLarge: 'Plik jest większy niż {{maxSize}} MB. Podziel pytania na kilka plików.',
        tooManyRows_few:
          'Plik ma {{count}} pytania, a naraz można zaimportować {{limit}}. Podziel pytania na kilka plików.',
        tooManyRows_many:
          'Plik ma {{count}} pytań, a naraz można zaimportować {{limit}}. Podziel pytania na kilka plików.',
        tooManyRows_one:
          'Plik ma {{count}} pytanie, a naraz można zaimportować {{limit}}. Podziel pytania na kilka plików.',
        tooManyRowsNoCount:
          'Plik ma więcej pytań, niż można zaimportować naraz ({{limit}}). Podziel pytania na kilka plików.',
        unreadable:
          'Nie udało się odczytać pliku. Użyj szablonu i nie zmieniaj w nim nazwy arkusza <strong>Pytania</strong> ani nagłówków kolumn.',
      },
      modes: {
        [ImportDialogModeEnum.CONVERSION]: 'Konwersja z dotychczasowego formatu',
        [ImportDialogModeEnum.IMPORT]: 'Import z szablonu',
      },
      modesLabel: 'Sposób wgrania pytań',
      pick: {
        description:
          'Wgraj plik przygotowany na szablonie. Każdy wiersz zostanie najpierw sprawdzony, a pytania zapiszą się dopiero po potwierdzeniu.',
        dropzone: 'Przeciągnij plik tutaj albo kliknij, aby go wybrać',
        limits: 'Plik .xlsx, najwyżej {{maxSize}} MB i {{maxRows}} pytań',
        notes:
          'Zdjęcia i filmy dodasz w edytorze pytania po imporcie. Pytanie z kluczem, który jest już w bazie, nie zostanie nadpisane; zmiany w nim wprowadź w formularzu.',
      },
      report: {
        activated: 'Od razu aktywne',
        columns: {
          column: 'Kolumna',
          problem: 'Problem',
          row: 'Wiersz',
          sheet: 'Arkusz',
        },
        drafts: 'Jako szkice',
        empty:
          'Plik nie zawiera pytań. Uzupełnij arkusz <strong>Pytania</strong> i wgraj plik ponownie.',
        errorsDescription:
          'Pytania zapiszą się tylko wtedy, gdy wszystkie wiersze są poprawne. Popraw wskazane miejsca w pliku i wgraj go ponownie.',
        errorsTitle_few: 'W pliku są {{count}} błędy',
        errorsTitle_many: 'W pliku jest {{count}} błędów',
        errorsTitle_one: 'W pliku jest {{count}} błąd',
        file: 'Plik: {{name}}',
        listLabel: 'Błędy w pliku',
        readyDraftsOnly: 'Po potwierdzeniu pytania zapiszą się jako szkice.',
        readyTitle: 'Plik jest gotowy do importu',
        readyWithActive:
          'Pytania zapiszą się po potwierdzeniu. Aktywne pytania od razu mogą trafić do testów.',
        rows: 'Pytania w pliku',
        toFix: 'Do poprawy',
        wholeRow: 'Cały wiersz',
      },
      template: {
        description: 'Opis kolumn i przykładowe pytania.',
        download: 'Pobierz szablon',
        failed: 'Nie udało się pobrać szablonu. Spróbuj ponownie.',
        title: 'Szablon pliku',
      },
      title: 'Import pytań z Excela',
    },
    languageStatus: '{{language}}: {{status}}',
    noSummary: 'Brak treści',
    positionsCell: {
      all: 'Wszystkie stanowiska',
      collapse: 'Zwiń',
      more_few: '+{{count}} stanowiska',
      more_many: '+{{count}} stanowisk',
      more_one: '+{{count}} stanowisko',
      outdated: 'Nieaktualne',
    },
    retireDialog: {
      confirm: 'Wyłącz',
      description:
        'Pytanie przestanie pojawiać się w testach. Wyłączona wersja zostaje w jego historii.',
      done: 'Pytanie {{key}} zostało wyłączone.',
      failed: 'Nie udało się wyłączyć pytania. Spróbuj ponownie.',
      title: 'Wyłączyć pytanie {{key}}?',
    },
    review: 'Przegląd',
    reviewMarker: 'Do przeglądu',
    search: {
      label: 'Szukaj po treści lub kluczu pytania',
      language: 'Język',
      noMatch:
        'Szukaj po treści lub kluczu pytania albo wyczyść filtry, aby zobaczyć więcej pytań.',
      placeholder: 'Szukaj po treści lub kluczu',
    },
    tableLabel: 'Pytania',
    title: 'Baza pytań',
  },
  questionFocus: {
    backToList: 'Wróć do listy',
    description:
      'Jedno pytanie na ekranie, tak jak zobaczy je pracownik. Strzałki przełączają pytania, Esc wraca do listy.',
    empty: {
      clearFilters: 'Wyczyść filtry',
      description: 'Dodaj pytanie, a pojawi się tutaj do przeglądu.',
      noMatchDescription: 'Zmień albo wyczyść filtry, aby zobaczyć więcej pytań.',
      noMatchSearch:
        'Szukaj po treści lub kluczu pytania albo wyczyść filtry, aby zobaczyć więcej pytań.',
      noMatchTitle: 'Żadne pytanie nie pasuje do filtrów',
      title: 'Nie ma pytań do przeglądu',
    },
    navigation: {
      counter: '{{position}} z {{total}}',
      edit: 'Edytuj',
      next: 'Następne',
      previous: 'Poprzednie',
    },
    notFound: {
      description: 'Pytanie mogło zostać usunięte. Przejdź do następnego.',
      title: 'To pytanie jest już niedostępne',
    },
    sort: {
      label: 'Kolejność',
      options: {
        [QuestionSortKeyEnum.BUSINESS_KEY]: { asc: 'Klucz od A do Z', desc: 'Klucz od Z do A' },
        [QuestionSortKeyEnum.UPDATED_AT]: {
          asc: 'Najdawniej zmienione',
          desc: 'Ostatnio zmienione',
        },
        [QuestionSortKeyEnum.VERSION_NO]: { asc: 'Najniższa wersja', desc: 'Najwyższa wersja' },
      },
    },
    title: 'Przegląd pytań',
  },
  questionForm: {
    actions: {
      fixTypo: 'Popraw literówkę',
      fixTypoHint: 'Zapisuje zmiany w bieżącej wersji.',
      newVersion: 'Nowa wersja',
      newVersionHint: 'Użyj, gdy zmienia się sens pytania.',
      saveDraft: 'Zapisz szkic',
      saveDraftHint: 'Zapisuje zmiany w tym szkicu.',
      status: {
        clean: 'Brak zmian',
        cleanDescription: 'Zmień pytanie powyżej, a potem wybierz sposób zapisu.',
        cleanDraftDescription: 'Zmień pytanie powyżej, a potem zapisz szkic.',
        dirty: 'Niezapisane zmiany',
        dirtyDescription:
          'Poprawka literówki zostaje w bieżącej wersji. Zmiana sensu pytania wymaga nowej wersji, a obecna zostanie wyłączona.',
        dirtyDraftDescription: 'Zapisz szkic, aby zachować zmiany.',
        locked: 'Ta wersja jest wyłączona',
        lockedDescription: 'Zmiany zapiszesz jako nową wersję.',
      },
    },
    answers: {
      add: 'Dodaj odpowiedź',
      answerLabel: 'Odpowiedź {{number}}',
      answerPlaceholder: 'Treść odpowiedzi',
      descriptionMultiple: 'Zaznacz wszystkie poprawne odpowiedzi.',
      descriptionOrdering:
        'Ułóż odpowiedzi we wzorcowej kolejności, przeciągając je albo używając strzałek. Pracownik zobaczy je w losowej kolejności.',
      descriptionSingle: 'Zaznacz jedną poprawną odpowiedź.',
      descriptionSurvey: 'Pytania ankietowe nie mają poprawnej odpowiedzi.',
      markCorrect: 'Oznacz odpowiedź {{number}} jako poprawną',
      moveDown: 'Przesuń odpowiedź {{number}} niżej',
      moveUp: 'Przesuń odpowiedź {{number}} wyżej',
      position: 'Pozycja {{number}} we wzorcowej kolejności',
      remove: 'Usuń odpowiedź {{number}}',
      removeDisabled: 'Pytanie musi mieć co najmniej dwie odpowiedzi.',
      title: 'Odpowiedzi',
    },
    backToBank: 'Wróć do bazy pytań',
    backToFocus: 'Wróć do przeglądu',
    classification: {
      addTag: 'Dodaj tag „{{label}}”',
      businessKey: 'Klucz pytania',
      businessKeyAuto: 'Klucz zostanie nadany automatycznie, na przykład {{example}}.',
      businessKeyAutoNoCategory: 'Klucz zostanie nadany automatycznie po wybraniu kategorii.',
      businessKeyManual: 'Wpisz klucz ręcznie',
      businessKeyManualHelper:
        'Tylko dla pytań przenoszonych z istniejącej bazy. Najwyżej {{max}} znaków.',
      categoriesUnavailable: 'Nie udało się wczytać kategorii. Odśwież stronę.',
      category: 'Kategoria',
      categoryPlaceholder: 'Wybierz kategorię',
      description: 'Klasyfikację można później zmienić bez tworzenia nowej wersji.',
      noPositions: 'Brak pasujących stanowisk',
      noTags: 'Brak pasujących tagów',
      outdatedNotice_few:
        '{{count}} stanowiska są nieaktualne: zniknęły z jAIn albo zostały tam wyłączone. Pytanie je zachowuje, ale nie można ich przypisać do innych pytań. Usuń je, jeśli pytanie nie ma już do nich trafiać.',
      outdatedNotice_many:
        '{{count}} stanowisk jest nieaktualnych: zniknęły z jAIn albo zostały tam wyłączone. Pytanie je zachowuje, ale nie można ich przypisać do innych pytań. Usuń je, jeśli pytanie nie ma już do nich trafiać.',
      outdatedNotice_one:
        'Jedno stanowisko jest nieaktualne: zniknęło z jAIn albo zostało tam wyłączone. Pytanie je zachowuje, ale nie można go przypisać do innych pytań. Usuń je, jeśli pytanie nie ma już do niego trafiać.',
      outdatedPosition: '{{label}} (nieaktualne)',
      positions: 'Stanowiska',
      positionScope: 'Dla kogo jest pytanie',
      positionScopes: {
        [PositionScopeEnum.ALL]: {
          description: 'Pytanie może trafić do każdego pracownika, niezależnie od stanowiska.',
          label: 'Wszystkie stanowiska',
        },
        [PositionScopeEnum.SELECTED]: {
          description: 'Pytanie trafi tylko do pracowników na wybranych stanowiskach.',
          label: 'Wybrane stanowiska',
        },
      },
      positionsLoading: 'Wczytywanie stanowisk',
      positionsPlaceholder: 'Szukaj po nazwie albo numerze',
      positionsUnavailable: 'Nie udało się wczytać stanowisk. Odśwież stronę.',
      sources: {
        add: 'Dodaj źródło',
        addDisabled: 'Pytanie może mieć najwyżej {{max}} źródeł.',
        addLink: 'Dodaj link',
        addLinkDisabled: 'Procedura może mieć najwyżej {{max}} linków.',
        description: 'Opis',
        descriptionPlaceholder: 'Na przykład: Rozdział 3, przechowywanie towaru',
        item: 'Źródło {{number}}',
        label: 'Źródła',
        linkDescription: 'Do czego prowadzi link',
        linkDescriptionPlaceholder: 'Na przykład: PDF procedury',
        links: 'Linki',
        linkUrl: 'Adres',
        linkUrlPlaceholder: 'https://',
        noLinks: 'Brak linków. Dodaj link do dokumentu procedury albo filmu instruktażowego.',
        openLink: 'Otwórz link {{number}} w nowej karcie',
        procedureName: 'Nazwa procedury',
        procedureNamePlaceholder: 'Na przykład: Procedura przyjęcia towaru',
        remove: 'Usuń źródło {{number}}',
        removeDisabled: 'Pytanie potrzebuje co najmniej jednego źródła.',
        removeLink: 'Usuń link {{number}}',
        type: 'Typ źródła',
        typePlaceholder: 'Wybierz typ źródła',
      },
      tagCreateError: 'Nie udało się dodać tagu. Spróbuj ponownie.',
      tags: 'Tagi',
      tagsPlaceholder: 'Wpisz, aby wyszukać',
      tagsUnavailable: 'Nie udało się wczytać tagów. Odśwież stronę.',
      title: 'Klasyfikacja',
    },
    content: {
      body: 'Treść pytania',
      bodyPlaceholder: 'Na przykład: Jak długo można przechowywać otwarte mleko w chłodni?',
      description: 'Wybierz sposób odpowiedzi i wpisz treść pytania.',
      explanation: 'Wyjaśnienie po teście',
      explanationHelper: 'Opcjonalne. Pojawia się po teście, jeśli test ma włączone wyjaśnienia.',
      purpose: 'Przeznaczenie',
      purposeHelper: 'Pytanie testowe trafia tylko do testów, ankietowe tylko do ankiet.',
      purposeLocked: 'Przeznaczenia nie można zmienić po utworzeniu pytania.',
      title: 'Treść',
      type: 'Typ pytania',
      typeLocked: 'Typu nie można zmienić po utworzeniu pytania.',
    },
    create: {
      description: 'Szkic możesz zapisać w dowolnym momencie i dokończyć pytanie później.',
      duplicateDescription: 'Kopia pytania {{key}}. Zapisz ją jako nowy szkic.',
      duplicateNotFound: {
        description: 'Link mógł stracić ważność. Wróć do bazy pytań i wybierz pytanie z listy.',
        title: 'Nie znaleziono pytania do skopiowania',
      },
      title: 'Nowe pytanie',
    },
    edit: {
      activateDialog: {
        confirm: 'Aktywuj',
        description:
          'Od teraz testy i ankiety będą korzystać z tej wersji. Poprzednie wersje zostają w historii pytania.',
        title: 'Aktywować wersję {{number}}?',
      },
      activateVersion: 'Aktywuj wersję',
      classificationDialog: {
        confirm: 'Zapisz',
        description:
          'Zmieniasz kategorię lub stanowiska. Od nich zależy, w których testach i ankietach pojawia się pytanie.',
        title: 'Zapisać zmiany?',
      },
      draftBesideActive: {
        description: 'Testy korzystają z niej do czasu aktywacji tego szkicu.',
        title: 'Wersja {{number}} pozostaje w użyciu',
      },
      history: 'Historia wersji',
      noEditableVersion: {
        description:
          'Wszystkie wersje tego pytania są wyłączone. Aby je zmienić, utwórz nową wersję.',
        title: 'Brak wersji do edycji',
      },
      notFound: {
        description: 'Link mógł stracić ważność. Wróć do bazy pytań i otwórz pytanie z listy.',
        title: 'Nie znaleziono pytania',
      },
      title: 'Edycja pytania',
      versionLabel: 'Wersja {{number}}',
    },
    errors: {
      activateFailed: 'Nie udało się aktywować wersji. Spróbuj ponownie.',
      conflict: 'Pytanie zmieniło się w międzyczasie. Odśwież stronę i spróbuj ponownie.',
      forbidden: 'Nie masz uprawnień do zapisania tego pytania.',
      generatedKeyTaken:
        'Klucz nadany automatycznie jest już zajęty. Wpisz klucz ręcznie i zapisz ponownie.',
      keyTaken: 'Ten klucz jest już zajęty. Wpisz inny.',
      notFound: 'To pytanie już nie istnieje. Wróć do bazy pytań.',
    },
    examinerKey: {
      answerKeyHelper: 'Pracownik nigdy nie widzi tego tekstu.',
      commentRequired: 'Egzaminator musi dodać komentarz',
      commentRequiredHelper:
        'Gdy ta opcja jest włączona, egzaminator nie zapisze wyniku bez komentarza.',
      descriptions: {
        [QuestionTypeDto.OpenText]:
          'Pracownik pisze odpowiedź własnymi słowami, a egzaminator ocenia ją według tego klucza.',
        [QuestionTypeDto.PassFail]:
          'Pytanie nie ma wariantów odpowiedzi. Egzaminator stwierdza, czy pracownik spełnił kryterium.',
        [QuestionTypeDto.Practical]:
          'Wskazówki dla egzaminatora, na co zwrócić uwagę podczas zadania.',
      },
      examinerOnly: 'Tylko dla egzaminatora',
      labels: {
        [QuestionTypeDto.OpenText]: 'Klucz odpowiedzi',
        [QuestionTypeDto.PassFail]: 'Opis kryterium',
        [QuestionTypeDto.Practical]: 'Klucz odpowiedzi',
      },
      placeholders: {
        [QuestionTypeDto.OpenText]:
          'Na przykład: odpowiedź podaje temperaturę przechowywania i termin przydatności.',
        [QuestionTypeDto.PassFail]:
          'Na przykład: pracownik zakłada rękawice i fartuch przed wejściem na halę.',
        [QuestionTypeDto.Practical]: 'Na przykład: sprawdź kolejność czynności i zasady BHP.',
      },
      titles: {
        [QuestionTypeDto.OpenText]: 'Klucz odpowiedzi',
        [QuestionTypeDto.PassFail]: 'Kryterium zaliczenia',
        [QuestionTypeDto.Practical]: 'Klucz odpowiedzi',
      },
    },
    expectedAnswers: {
      add: 'Dodaj oczekiwaną odpowiedź',
      addDisabled: 'Pytanie może mieć najwyżej {{max}} oczekiwanych odpowiedzi.',
      answerLabel: 'Oczekiwana odpowiedź {{number}}',
      answerPlaceholder: 'Na przykład: 4',
      description:
        'Pracownik wpisuje odpowiedź, a system porównuje ją z tą listą. Wielkość liter i zbędne spacje się nie liczą, każda inna różnica już tak. Dlatego „4” i „4,0” to dwie różne odpowiedzi. Jeśli obie są poprawne, dodaj obie.',
      remove: 'Usuń oczekiwaną odpowiedź {{number}}',
      removeDisabled: 'Pytanie potrzebuje co najmniej jednej oczekiwanej odpowiedzi.',
      title: 'Oczekiwane odpowiedzi',
    },
    formPreview: {
      description:
        'Tak pytanie zobaczy pracownik, razem z niezapisanymi zmianami. Odpowiedzi w podglądzie nie są zapisywane.',
      hide: 'Ukryj podgląd',
      show: 'Podgląd',
      title: 'Podgląd',
    },
    media: {
      actions: {
        remove: 'Usuń {{name}}',
      },
      answerLabel: 'Materiał odpowiedzi {{number}}',
      copied: {
        description:
          'Zdjęcia i filmy z pytania i odpowiedzi zapiszą się razem z kopią. Jeśli któregoś nie potrzebujesz, usuń go po zapisaniu szkicu.',
        title: 'Materiał przejdzie do kopii',
      },
      deleteDialog: {
        cancel: 'Anuluj',
        confirm: 'Usuń',
        description: 'Materiał {{name}} zostanie usunięty z tej wersji pytania.',
        title: 'Usunąć materiał?',
      },
      description: 'Zdjęcie lub film, który pracownik zobaczy przy pytaniu.',
      disabled: {
        unsaved: 'Materiał dodasz po zapisaniu pytania.',
        unsavedAnswer: 'Materiał dodasz po zapisaniu tej odpowiedzi.',
      },
      dropzone: {
        answerLabel: 'Przeciągnij materiał do tej odpowiedzi albo kliknij, żeby go dodać',
        formats: 'Zdjęcie JPEG, PNG lub WebP albo film MP4',
        label: 'Przeciągnij zdjęcie lub film tutaj albo kliknij, żeby go dodać',
        limits: 'Zdjęcie do 5 MB i 4000 px na dłuższym boku, film do 20 MB, 90 s i 720p',
      },
      empty: 'Pytanie nie ma jeszcze materiału.',
      errors: {
        FILE_TOO_LARGE:
          'Plik ma {{actual}}, a limit to {{limit}}. Zmniejsz plik i dodaj go ponownie.',
        fileTooLargeWithoutActual:
          'Plik ma więcej niż {{limit}}. Zmniejsz plik i dodaj go ponownie.',
        fileTooLargeWithoutLimit: 'Plik jest za duży. Zdjęcie może mieć do 5 MB, a film do 20 MB.',
        forbidden: 'Nie masz uprawnień do zmiany materiału w tym pytaniu.',
        IMAGE_TOO_LARGE:
          'Dłuższy bok zdjęcia ma {{actual}} px, a limit to {{limit}} px. Zmniejsz zdjęcie i dodaj je ponownie.',
        notADraft: 'Materiał zmienisz tylko w szkicu. Utwórz nową wersję i dodaj materiał w niej.',
        outOfLimits:
          'Plik nie spełnia wymagań. Zdjęcia: JPEG, PNG lub WebP do 5 MB i 4000 px na dłuższym boku. Filmy: MP4 do 20 MB, 90 sekund i 720p.',
        removeFailed: 'Nie udało się usunąć materiału. Spróbuj ponownie.',
        unavailable: 'Materiały są teraz niedostępne. Spróbuj ponownie za chwilę.',
        unknown: 'Nie udało się dodać pliku. Spróbuj ponownie.',
        UNREADABLE:
          'Nie udało się odczytać pliku. Może być uszkodzony albo niepełny. Zapisz go ponownie i dodaj jeszcze raz.',
        UNSUPPORTED_TYPE:
          'Ten format pliku nie jest obsługiwany. Dodaj zdjęcie JPEG, PNG lub WebP albo film MP4.',
        unsupportedHeic:
          'Zdjęcia HEIC z telefonu nie są obsługiwane. Zapisz zdjęcie jako JPEG i dodaj je ponownie.',
        unsupportedQuickTime:
          'Filmy MOV nie są obsługiwane. Zapisz film jako MP4 i dodaj go ponownie.',
        VIDEO_CODEC_UNSUPPORTED:
          'Przeglądarka nie odtworzy tego filmu. Zapisz go jako MP4 z obrazem H.264 i dźwiękiem AAC, a potem dodaj ponownie.',
        VIDEO_NOT_FASTSTART:
          'Film musi się pobrać w całości, zanim ruszy. Wyeksportuj go z włączoną opcją Fast start lub Optymalizuj dla sieci i dodaj ponownie.',
        VIDEO_RESOLUTION_TOO_HIGH:
          'Film ma rozdzielczość {{actual}}p, a limit to {{limit}}p. Wyeksportuj go w niższej jakości i dodaj ponownie.',
        VIDEO_TOO_LONG:
          'Film trwa {{actual}}, a limit to {{limit}}. Skróć film i dodaj go ponownie.',
      },
      kind: {
        IMAGE: 'Zdjęcie',
        VIDEO: 'Film',
      },
      notifications: {
        removed: 'Usunięto materiał.',
        uploaded: 'Dodano materiał.',
      },
      problems: {
        fileTooLargeWithoutActual: 'Plik ma więcej niż {{limit}}.',
        fileTooLargeWithoutLimit: 'Plik jest za duży. Zdjęcie może mieć do 5 MB, a film do 20 MB.',
        [MediaRejectionReasonDto.FileTooLarge]: 'Plik ma {{actual}}, a limit to {{limit}}.',
        [MediaRejectionReasonDto.ImageTooLarge]:
          'Dłuższy bok zdjęcia ma {{actual}} px, a limit to {{limit}} px.',
        [MediaRejectionReasonDto.Unreadable]:
          'Nie udało się odczytać pliku. Może być uszkodzony albo niepełny.',
        [MediaRejectionReasonDto.UnsupportedType]:
          'Ten format pliku nie jest obsługiwany. Zapisz zdjęcie jako JPEG, PNG lub WebP albo film jako MP4.',
        [MediaRejectionReasonDto.VideoCodecUnsupported]:
          'Przeglądarka nie odtworzy tego filmu. Zapisz go jako MP4 z obrazem H.264 i dźwiękiem AAC.',
        [MediaRejectionReasonDto.VideoNotFaststart]:
          'Film musi się pobrać w całości, zanim ruszy. Wyeksportuj go z włączoną opcją Fast start lub Optymalizuj dla sieci.',
        [MediaRejectionReasonDto.VideoResolutionTooHigh]:
          'Film ma rozdzielczość {{actual}}p, a limit to {{limit}}p.',
        [MediaRejectionReasonDto.VideoTooLong]: 'Film trwa {{actual}}, a najdłużej {{limit}}.',
        nextStep: 'Popraw plik i dodaj go ponownie.',
        outOfLimits:
          'Plik nie spełnia wymagań. Zdjęcia: JPEG, PNG lub WebP do 5 MB i 4000 px na dłuższym boku. Filmy: MP4 do 20 MB, 90 sekund i 720p.',
        unsupportedHeic: 'To zdjęcie HEIC. Zapisz je jako JPEG.',
        unsupportedQuickTime: 'To film QuickTime (.mov). Zapisz go jako MP4.',
      },
      readOnly: {
        description:
          'Materiał zmienia się tylko w szkicu, żeby pracownicy widzieli to samo co w teście. Aby go zmienić, utwórz nową wersję.',
        title: 'Materiał zmienisz w nowej wersji',
      },
      title: 'Materiał',
      units: {
        dimensions: '{{width}} × {{height}} px',
        megabytes: '{{value}} MB',
        minutes: '{{minutes}} min',
        minutesSeconds: '{{minutes}} min {{seconds}} s',
        seconds: '{{seconds}} s',
      },
      uploading: 'Dodawanie materiału {{name}}',
    },
    newVersionDialog: {
      cancel: 'Anuluj',
      chooseDescription:
        'Wybierz, co stanie się z wersją w użyciu. Jeśli poprawiasz tylko literówkę, wybierz Popraw literówkę.',
      confirm: 'Utwórz nową wersję',
      description:
        'Obecna wersja zostanie wyłączona. Jeśli poprawiasz tylko literówkę, wybierz Popraw literówkę.',
      modes: {
        [NewVersionModeEnum.KEEP_CURRENT]: {
          description:
            'Pytanie zostaje w testach w obecnym brzmieniu. Nowa wersja zastąpi je po aktywacji.',
          label: 'Stara wersja działa do aktywacji nowej',
        },
        [NewVersionModeEnum.RETIRE_NOW]: {
          description:
            'Na przykład gdy zmieniła się procedura. Pytanie wypada z testów do czasu aktywacji nowej wersji.',
          label: 'Wyłącz starą wersję od razu',
        },
      },
      title: 'Utworzyć nową wersję?',
    },
    notifications: {
      activated: 'Wersja jest teraz aktywna.',
      classificationSaved: 'Zapisano klasyfikację.',
      created: 'Zapisano szkic.',
      typoFixed: 'Zapisano poprawkę.',
      versionCreated: 'Utworzono nową wersję.',
    },
    page: {
      title: 'Pytanie {{key}}',
      views: {
        [QuestionPageViewEnum.EDIT]: 'Edycja',
        [QuestionPageViewEnum.HISTORY]: 'Historia wersji',
        [QuestionPageViewEnum.STATISTICS]: 'Statystyki',
      },
      viewsLabel: 'Widok pytania',
    },
    practical: {
      add: 'Dodaj zagadnienie',
      addDisabled: 'Pytanie może mieć najwyżej {{max}} zagadnień.',
      description:
        'Egzaminator wybiera część zagadnień z tej listy do zadania i ocenia wynik w skali poniżej.',
      remove: 'Usuń zagadnienie {{number}}',
      removeDisabled: 'Pytanie praktyczne potrzebuje co najmniej jednego zagadnienia.',
      scaleMax: 'Skala od 0 do',
      scaleMaxHelper: 'Najwyższa ocena, jaką może dać egzaminator, od {{min}} do {{max}}.',
      title: 'Zagadnienia',
      topicLabel: 'Zagadnienie {{number}}',
      topicPlaceholder: 'Na przykład: Przyjęcie dostawy chłodniczej',
      topicsToPick: 'Ile zagadnień wybiera egzaminator',
      topicsToPickHelper: 'Od {{min}} do {{max}}, najwyżej tyle, ile jest zagadnień.',
    },
    ruleErrors: {
      [QuestionRuleDto.AnswerPointsPrecision]:
        'Wartości odpowiedzi mogą mieć najwyżej jedno miejsce po przecinku.',
      [QuestionRuleDto.AnswerPointsRange]:
        'Wartości odpowiedzi muszą mieścić się w zakresie od 0 do 100.',
      [QuestionRuleDto.BodyMissing]: 'Wpisz treść pytania.',
      [QuestionRuleDto.BodyTooLong]: 'Treść pytania może mieć najwyżej 2000 znaków.',
      [QuestionRuleDto.CorrectOptionMissing]: 'Zaznacz co najmniej jedną poprawną odpowiedź.',
      [QuestionRuleDto.MaxPointsNotPositive]: 'Liczba punktów musi być większa od zera.',
      [QuestionRuleDto.MaxPointsPrecision]:
        'Liczba punktów może mieć najwyżej jedno miejsce po przecinku, na przykład 2 albo 2,5.',
      [QuestionRuleDto.MaxPointsTooHigh]: 'Liczba punktów musi być mniejsza niż 10 000.',
      [QuestionRuleDto.OrderPlaceMissing]:
        'Nadaj każdej odpowiedzi miejsce w poprawnej kolejności.',
      [QuestionRuleDto.OrderPlacesInvalid]:
        'Miejsca w poprawnej kolejności muszą być liczbami od 1 do liczby odpowiedzi, bez powtórzeń.',
      [QuestionRuleDto.ScaleMaxMissing]: 'Podaj najwyższą ocenę na skali egzaminatora.',
      [QuestionRuleDto.ScaleMaxRange]: 'Najwyższa ocena na skali musi być liczbą od 1 do 10.',
      [QuestionRuleDto.SingleChoiceManyCorrect]:
        'Jednokrotny wybór ma dokładnie jedną poprawną odpowiedź, a zaznaczono więcej. Zostaw jedną albo zmień typ na wielokrotny wybór.',
      [QuestionRuleDto.TopicsToPickMissing]: 'Podaj, ile zagadnień wybiera egzaminator.',
      [QuestionRuleDto.TopicsToPickRange]:
        'Egzaminator musi wybrać co najmniej jedno zagadnienie i nie więcej, niż jest w pytaniu.',
    },
    scoring: {
      description: 'Ile punktów jest warte pytanie i jak się je liczy.',
      maxPoints: 'Liczba punktów',
      maxPointsHelper: 'Najwyżej jedno miejsce po przecinku, na przykład 2,5.',
      notes: {
        examinerGraded: 'Punkty przyznaje egzaminator.',
        expectedAnswer:
          'Pytanie daje komplet punktów, gdy odpowiedź zgadza się z którąkolwiek oczekiwaną odpowiedzią, a w przeciwnym razie zero.',
        singleChoice:
          'Przy jednokrotnym wyborze poprawna odpowiedź daje komplet punktów, a każda inna zero.',
      },
      scoringRule: 'Reguła punktacji',
      title: 'Punktacja',
    },
    serverErrors: {
      answerBody: 'Wpisz treść odpowiedzi.',
      answerKey: 'Sprawdź tekst dla egzaminatora.',
      answerPoints:
        'Wartości odpowiedzi muszą mieścić się w zakresie od 0 do 100 i mieć najwyżej jedno miejsce po przecinku.',
      answers: 'Sprawdź odpowiedzi. Pytanie potrzebuje co najmniej dwóch odpowiedzi z treścią.',
      answersCorrect: 'Zaznacz co najmniej jedną poprawną odpowiedź.',
      answersOrder:
        'Sprawdź kolejność odpowiedzi. Pytanie potrzebuje co najmniej dwóch odpowiedzi, każdej na innej pozycji.',
      body: 'Wpisz treść pytania.',
      businessKey: 'Klucz pytania może mieć najwyżej 40 znaków. Skróć go.',
      categoryId: 'Wybierz kategorię.',
      expectedAnswers:
        'Sprawdź oczekiwane odpowiedzi. Każda musi mieć treść i może wystąpić tylko raz.',
      maxPoints:
        'Wpisz liczbę punktów większą od zera i mniejszą niż 10 000, z najwyżej jednym miejscem po przecinku.',
      positionCodes: 'Sprawdź wybrane stanowiska.',
      procedureName: 'Sprawdź nazwę procedury. Każda procedura może wystąpić w pytaniu tylko raz.',
      scaleMax: 'Wpisz skalę sięgającą co najmniej do 1.',
      scoringRule: 'Wybierz regułę punktacji.',
      source: 'Sprawdź to źródło. Każde źródło może wystąpić tylko raz.',
      sourceDescription: 'Sprawdź opis źródła.',
      sourceLinkDescription: 'Wpisz, do czego prowadzi link.',
      sourceLinks: 'Sprawdź linki. Każdy adres może wystąpić w procedurze tylko raz.',
      sourceLinkUrl:
        'Wpisz pełny adres zaczynający się od http:// lub https://. Każdy adres może wystąpić tylko raz.',
      sources: 'Sprawdź źródła. Pytanie potrzebuje co najmniej jednego.',
      tags: 'Sprawdź wybrane tagi.',
      topics: 'Sprawdź zagadnienia. Każde musi mieć treść i może wystąpić tylko raz.',
      topicsToPick: 'Egzaminator nie może wybrać więcej zagadnień, niż jest na liście.',
    },
    translations: {
      answers: 'Odpowiedzi',
      description: 'Stan pytania w każdym języku.',
      missing: 'Brak tłumaczenia.',
      sourceLanguage: 'Język źródłowy. Treść edytujesz w sekcji Treść powyżej.',
      title: 'Tłumaczenia',
    },
    unsavedChanges: {
      description: 'Zmiany, których nie zapisano, zostaną utracone.',
      leave: 'Wyjdź bez zapisywania',
      stay: 'Zostań',
      title: 'Masz niezapisane zmiany. Wyjść bez zapisywania?',
    },
  },
  questionHistory: {
    classification: {
      description:
        'Zmiany kategorii, źródeł, tagów, stanowisk i oznaczenia do przeglądu. Dotyczą pytania, a nie jednej wersji.',
      empty: 'Klasyfikacja nie zmieniła się od utworzenia pytania.',
      title: 'Klasyfikacja',
    },
    compare: {
      answerOrder: 'Miejsce: {{value}}',
      answerPoints: 'Punkty: {{value}}',
      answers: 'Odpowiedzi',
      body: 'Treść',
      expectedAnswers: 'Oczekiwane odpowiedzi',
      explanation: 'Wyjaśnienie po teście',
      from: 'Wersja wcześniejsza',
      maxPoints: 'Liczba punktów',
      needTwo: 'Pytanie ma tylko jedną wersję, więc nie ma czego porównać.',
      noChanges: 'Te wersje mają taką samą treść, odpowiedzi i punktację.',
      scoringRule: 'Reguła punktacji',
      to: 'Wersja późniejsza',
    },
    created: 'Utworzono {{date}}, autor: {{author}}',
    description: 'Najnowsza wersja jest na górze, każda z listą zmian.',
    eventMeta: '{{date}}, {{author}}',
    events: {
      [HistoryEventKindDto.ClassificationChanged]: 'Zmieniono klasyfikację',
      [HistoryEventKindDto.Corrected]: 'Poprawiono w miejscu',
      [HistoryEventKindDto.Created]: 'Utworzono wersję',
      [HistoryEventKindDto.MediaAdded]: 'Dodano materiał',
      [HistoryEventKindDto.MediaRemoved]: 'Usunięto materiał',
      [HistoryEventKindDto.ReviewChanged]: 'Zmieniono oznaczenie do przeglądu',
      [HistoryEventKindDto.StatusChanged]: 'Zmieniono status',
    },
    fieldInLanguage: '{{field}} ({{language}})',
    fields: {
      allPositions: 'Dla wszystkich stanowisk',
      answer: 'Odpowiedź',
      'answer.body': 'Treść odpowiedzi',
      'answer.correctOrder': 'Miejsce w poprawnej kolejności',
      'answer.displayOrder': 'Kolejność odpowiedzi',
      'answer.isCorrect': 'Poprawna odpowiedź',
      'answer.points': 'Punkty za odpowiedź',
      answerKey: 'Klucz dla egzaminatora',
      body: 'Treść',
      category: 'Kategoria',
      examinerCommentRequired: 'Wymagany komentarz egzaminatora',
      expectedAnswers: 'Oczekiwane odpowiedzi',
      explanation: 'Wyjaśnienie po teście',
      maxPoints: 'Liczba punktów',
      media: 'Materiał',
      positionCodes: 'Stanowiska',
      review: 'Oznaczenie do przeglądu',
      scaleMax: 'Górna granica skali',
      scoringRule: 'Reguła punktacji',
      source: 'Źródło',
      sourceLocale: 'Język źródłowy',
      sourceName: 'Nazwa źródła',
      sources: 'Źródła',
      status: 'Status',
      tags: 'Tagi',
      topics: 'Tematy',
      topicsToPick: 'Liczba tematów do wyboru',
      translation: 'Język',
      translationStatus: 'Status treści',
    },
    notFound: {
      description: 'Link mógł stracić ważność. Wróć do bazy pytań i otwórz pytanie z listy.',
      title: 'Nie znaleziono pytania',
    },
    otherField: 'Inna zmiana',
    panel: {
      compare: 'Porównanie',
      description: 'Treść dokładnie taka, jaka obowiązywała w tej wersji.',
      modeLabel: 'Widok wersji',
      preview: 'Podgląd',
      title: 'Wersja {{number}}',
    },
    preview: {
      answers: 'Odpowiedzi',
      body: 'Treść',
      correct: 'Poprawna',
      expectedAnswers: 'Oczekiwane odpowiedzi',
      explanation: 'Wyjaśnienie po teście',
      maxPoints: 'Liczba punktów',
      scoringRule: 'Reguła punktacji',
    },
    readOnly:
      'Historii nie można zmienić. Aby wrócić do starszej treści, utwórz nową wersję w formularzu pytania.',
    retired: 'Wyłączono {{date}}',
    title: 'Historia pytania',
    values: {
      added: 'Dodano',
      contentStatus: {
        [TranslationStatusDto.Approved]: 'zatwierdzona',
        [TranslationStatusDto.Draft]: 'szkic',
        [TranslationStatusDto.Missing]: 'brak',
      },
      correct: 'poprawna',
      empty: 'brak',
      incorrect: 'niepoprawna',
      item: 'element',
      no: 'nie',
      removed: 'Usunięto',
      review: '{{reason}} ({{person}})',
      reviewWithoutReason: 'Oznaczenie: {{person}}',
      yes: 'tak',
    },
    versionLabel: 'Wersja {{number}}',
    versions: {
      description: 'Wybierz wersję, aby zobaczyć jej treść.',
      title: 'Wersje',
    },
  },
  questionStatistics: {
    categories: {
      columns: {
        asked: 'Liczba razy',
        averagePercent: 'Średni wynik',
        category: 'Kategoria',
        correctPercent: 'Poprawne',
        questions: 'Pytania',
      },
      description:
        'Pytania zsumowane według kategorii dla tego samego okresu, testu i typu. Filtry kategorii i minimalnej liczby razy tu nie działają.',
      emptyDescription: 'W tym okresie nie padło żadne pytanie z żadnej kategorii.',
      emptyTitle: 'Brak kategorii do pokazania',
      tableLabel: 'Skuteczność pytań według kategorii',
      title: 'Według kategorii',
    },
    exportAction: {
      empty: 'Dla tych filtrów nie ma czego eksportować.',
      failed: 'Nie udało się wyeksportować raportu. Spróbuj ponownie.',
      label: 'Eksportuj do Excela',
    },
    filters: {
      allCategories: 'Wszystkie kategorie',
      allTests: 'Wszystkie testy',
      allTypes: 'Wszystkie typy',
      anyCount: 'Dowolna liczba razy',
      category: 'Kategoria',
      fromMonth: 'Od miesiąca',
      minAsked: 'Minimalna liczba razy',
      minAskedOption_few: 'Co najmniej {{count}} razy',
      minAskedOption_many: 'Co najmniej {{count}} razy',
      minAskedOption_one: 'Co najmniej {{count}} raz',
      test: 'Test',
      toMonth: 'Do miesiąca',
      type: 'Typ pytania',
    },
    noValue: '—',
    percent: '{{percent}}%',
    question: {
      description: 'Jak odpowiadano na to pytanie w testach i egzaminach.',
      empty: {
        description: 'Pytanie nie padło jeszcze w żadnym teście ani egzaminie.',
        title: 'Brak odpowiedzi',
      },
      noMatch: 'Pytanie nie padło w tym okresie ani w tym teście. Zmień filtry.',
      notFound: {
        description: 'Pytanie mogło zostać usunięte. Wróć do bazy pytań.',
        title: 'Nie znaleziono pytania',
      },
      options: {
        chosen_few: 'Wybrana {{count}} razy',
        chosen_many: 'Wybrana {{count}} razy',
        chosen_one: 'Wybrana {{count}} raz',
        correct: 'Poprawna',
        title: 'Wybrane odpowiedzi',
      },
      summary: 'Wszystkie wersje',
      version: 'Wersja {{number}}',
      versions: 'Według wersji',
    },
    ranking: {
      columns: {
        asked: 'Liczba razy',
        averagePercent: 'Średni wynik',
        category: 'Kategoria',
        correctPercent: 'Poprawne',
        question: 'Pytanie',
        type: 'Typ',
      },
      description: 'Jak pytania wypadają w testach i egzaminach, od najtrudniejszych.',
      empty: {
        description: 'Żadne pytanie nie padło jeszcze w zakończonym teście ani egzaminie.',
        title: 'Brak odpowiedzi',
      },
      noMatch: 'Żadne pytanie nie pasuje do tych filtrów. Poszerz okres albo wyczyść filtry.',
      tableLabel: 'Skuteczność pytań',
      title: 'Skuteczność pytań',
    },
    tally: {
      asked: 'Liczba razy',
      averagePercent: 'Średni wynik',
      correct: 'Poprawne',
      correctPercent: 'Poprawne odpowiedzi',
      partial: 'Częściowo poprawne',
      unanswered: 'Bez odpowiedzi',
      wrong: 'Błędne',
    },
  },
  settings: {
    check: {
      duration: {
        milliseconds: '{{value}} ms',
        seconds: '{{value}} s',
      },
      forbidden: 'Nie masz uprawnień do sprawdzania połączeń.',
      hint: 'Sprawdzenie niczego nie zapisuje, więc można je powtarzać.',
      label: 'Sprawdzenie',
      notRunYet: 'Połączenie nie było jeszcze sprawdzane. Wynik pojawi się tutaj, krok po kroku.',
      pending: 'Trwa sprawdzanie. Krok, który nie dostaje odpowiedzi, może potrwać kilka sekund.',
      requestFailed: 'Nie udało się uruchomić sprawdzenia. Spróbuj ponownie.',
      running: 'Sprawdzanie…',
      skippedHint: 'Pominięty, bo wcześniejszy krok się nie udał.',
      statuses: {
        [CheckStatusDto.Failed]: 'Nieudany',
        [CheckStatusDto.Passed]: 'Udany',
        [CheckStatusDto.Skipped]: 'Pominięty',
      },
      steps: {
        [CheckStepNameDto.Configuration]: 'Ustawienia',
        [CheckStepNameDto.Delete]: 'Usunięcie pliku próbnego',
        [CheckStepNameDto.Jain]: 'Odpowiedź jAIn',
        [CheckStepNameDto.Link]: 'Odczyt pliku przez link',
        [CheckStepNameDto.Token]: 'Token z Keycloak',
        [CheckStepNameDto.Write]: 'Zapis pliku próbnego',
      },
      summary: {
        failed: 'Połączenie nie działa',
        ok: 'Połączenie działa',
      },
      technicalDetail: 'Szczegóły techniczne',
    },
    description:
      'Połączenia Ankietera z jAIn i z magazynem plików. Każde z nich można sprawdzić krok po kroku.',
    details: {
      label: 'Dane połączenia',
      loadFailed: 'Nie udało się wczytać danych połączenia. Spróbuj ponownie.',
      notSet: 'Nie ustawiono',
    },
    jain: {
      checkAction: 'Sprawdź połączenie',
      description: 'Z jAIn pochodzi słownik stanowisk.',
      failures: {
        [CheckFailureDto.BucketNotFound]:
          'jAIn odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.ContentMismatch]:
          'jAIn odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.IssuerNotFound]:
          'Keycloak nie ma takiego realmu. Sprawdź adres dostawcy tożsamości.',
        [CheckFailureDto.LinkRefused]:
          'jAIn odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.MissingRole]: 'Konto nie ma roli ankieter-sync.',
        [CheckFailureDto.NotAvailable]: 'jAIn nie udostępnia jeszcze danych dla Ankietera.',
        [CheckFailureDto.NotConfigured]:
          'Brakuje ustawień potrzebnych do połączenia z jAIn. Ich nazwy znajdziesz w szczegółach technicznych.',
        [CheckFailureDto.Refused]: 'jAIn odmówił dostępu temu kontu.',
        [CheckFailureDto.RejectedCredentials]:
          'Keycloak nie przyjął danych klienta. Sprawdź identyfikator i sekret.',
        [CheckFailureDto.ServiceAccountDisabled]:
          'Klient nie może pobierać tokenów dla siebie. Włącz dla niego konto usługi w Keycloak.',
        [CheckFailureDto.TokenRejected]:
          'jAIn nie przyjmuje tokenów z tego Keycloak. Sprawdź, czy oba korzystają z tego samego realmu.',
        [CheckFailureDto.UnexpectedResponse]:
          'jAIn odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.Unreachable]:
          'Brak odpowiedzi pod podanym adresem. Sprawdź adres i czy usługa działa.',
        [CheckFailureDto.WrongRegion]:
          'jAIn odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
      },
      fields: {
        apiUrl: 'Adres jAIn',
        clientId: 'Identyfikator klienta',
        issuerUri: 'Dostawca tożsamości (Keycloak)',
      },
      notConfigured: {
        description:
          'Brakuje adresu jAIn, więc słownik stanowisk się nie odświeża. Uzupełnij ustawienia na serwerze Ankietera.',
        title: 'Połączenie z jAIn nie jest skonfigurowane',
      },
      title: 'Połączenie z jAIn',
    },
    media: {
      checkAction: 'Sprawdź magazyn',
      description: 'W magazynie plików są zapisywane zdjęcia i filmy dołączane do pytań.',
      failures: {
        [CheckFailureDto.BucketNotFound]: 'W magazynie nie ma takiego bucketu. Sprawdź jego nazwę.',
        [CheckFailureDto.ContentMismatch]:
          'Link zwrócił inną zawartość niż zapisany plik. Sprawdź adres dla przeglądarek.',
        [CheckFailureDto.IssuerNotFound]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.LinkRefused]:
          'Magazyn odrzucił link do pliku. Zwykle link jest podpisany dla adresu, pod którym magazyn nie odpowiada. Sprawdź adres dla przeglądarek.',
        [CheckFailureDto.MissingRole]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.NotAvailable]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.NotConfigured]:
          'Brakuje ustawień potrzebnych do połączenia z magazynem. Ich nazwy znajdziesz w szczegółach technicznych.',
        [CheckFailureDto.Refused]:
          'Magazyn odmówił dostępu. Sprawdź, czy klucze mają uprawnienia do tego bucketu.',
        [CheckFailureDto.RejectedCredentials]:
          'Magazyn nie przyjął kluczy dostępu. Sprawdź klucz i sekret.',
        [CheckFailureDto.ServiceAccountDisabled]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.TokenRejected]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.UnexpectedResponse]:
          'Magazyn plików odpowiedział inaczej, niż oczekiwano. Szczegóły techniczne pomogą ustalić przyczynę.',
        [CheckFailureDto.Unreachable]:
          'Magazyn plików nie odpowiada pod podanym adresem. Sprawdź adres i czy usługa działa.',
        [CheckFailureDto.WrongRegion]:
          'Zły region magazynu, więc zapis i linki do plików nie zadziałają. Sprawdź region w ustawieniach.',
      },
      fields: {
        bucket: 'Bucket',
        endpoint: 'Adres magazynu',
        publicEndpoint: 'Adres dla przeglądarek',
        publicEndpointSame: 'Taki sam jak adres magazynu',
        region: 'Region',
      },
      notConfigured: {
        description:
          'Brakuje adresu magazynu, więc do pytań nie można dodawać zdjęć ani filmów. Uzupełnij ustawienia na serwerze Ankietera.',
        title: 'Magazyn plików nie jest skonfigurowany',
      },
      title: 'Magazyn plików',
    },
    title: 'Ustawienia',
  },
  surveyResponse: {
    actions: {
      answerRequired: 'Odpowiedz na pytanie, aby przejść dalej.',
      interrupt: 'Przerwij ankietę',
      next: 'Następne',
      previous: 'Poprzednie',
      refuse: 'Odmawiam',
      submit: 'Wyślij ankietę',
    },
    anonymous: 'Ankieta jest anonimowa. Odpowiedzi nie są pokazywane z imieniem i nazwiskiem.',
    ended: {
      refused: {
        description:
          'Odpowiedzi nie zostały zapisane. Przekaż komputer osobie, która uruchomiła ankietę.',
        statusLabel: 'Ankieta zakończona',
        title: 'Ankieta się zakończyła',
      },
      submitted: {
        description:
          'Odpowiedzi zostały zapisane. Przekaż komputer osobie, która uruchomiła ankietę.',
        statusLabel: 'Ankieta wysłana',
        title: 'Dziękujemy za wypełnienie ankiety',
      },
    },
    header: {
      answered: 'Odpowiedzi: {{answered}} z {{total}}',
      questionOf: 'Pytanie {{number}} z {{total}}',
    },
    instruction: 'Instrukcja',
    interrupt: {
      cancel: 'Wróć do ankiety',
      confirm: 'Przerwij ankietę',
      description:
        'Dotychczasowe odpowiedzi nie zostaną zapisane. Do przerwanej ankiety nie można wrócić.',
      title: 'Przerwać ankietę?',
    },
    optional: 'Nieobowiązkowe',
    refuseFailed: 'Nie udało się zakończyć ankiety. Spróbuj ponownie.',
    saveFailed: 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.',
    submitFailed: 'Nie udało się wysłać ankiety. Spróbuj ponownie.',
    unavailable: {
      description:
        'Ankieta otwiera się tylko w przeglądarce, w której ją uruchomiono. Wróć do tamtej przeglądarki albo poproś o pomoc osobę, która uruchomiła ankietę.',
      statusLabel: 'Ankieta niedostępna',
      title: 'Tej ankiety nie można tu otworzyć',
    },
  },
  surveys: {
    actions: {
      copy: 'Kopiuj',
      delete: 'Usuń',
      deleteOnlyDraft: 'Usunąć można tylko szkic',
      edit: 'Edytuj',
      history: 'Historia zmian',
      menu: 'Akcje ankiety {{name}}',
      preview: 'Podgląd',
      responses: 'Wypełnienia',
      start: 'Uruchom ankietę',
      startOnlyActive: 'Uruchomić można tylko aktywną ankietę',
    },
    addSurvey: 'Nowa ankieta',
    answers: {
      anonymous: 'Anonimowe',
      named: 'Imienne',
    },
    columns: {
      actions: 'Akcje',
      answers: 'Odpowiedzi',
      name: 'Nazwa',
      questions: 'Pytania',
      status: 'Status',
      updatedAt: 'Zmieniono',
    },
    copied: 'Utworzono kopię „{{name}}”.',
    copyFailed: 'Nie udało się skopiować ankiety. Spróbuj ponownie.',
    copyForbidden: 'Nie masz uprawnień do kopiowania ankiet.',
    deleteDialog: {
      cancel: 'Anuluj',
      confirm: 'Usuń',
      description: 'Szkic zniknie z listy ankiet. Tej operacji nie można cofnąć.',
      done: 'Usunięto ankietę „{{name}}”.',
      failed: 'Nie udało się usunąć ankiety. Spróbuj ponownie.',
      forbidden: 'Nie masz uprawnień do usunięcia tej ankiety.',
      notDraft: 'Ta ankieta była już w użyciu, więc nie można jej usunąć.',
      title: 'Usunąć ankietę „{{name}}”?',
    },
    description:
      'Ankiety złożone z pytań ankietowych z bazy. Zawęź listę filtrami albo wyszukaj po nazwie.',
    empty: {
      description: 'Utwórz pierwszą ankietę i dodaj do niej pytania ankietowe z bazy.',
      title: 'Brak ankiet',
    },
    filters: {
      allCategories: 'Wszystkie kategorie',
      allStatuses: 'Każdy status',
      category: 'Kategoria',
      status: 'Status',
    },
    form: {
      actions: {
        cancel: 'Anuluj',
        save: 'Zapisz zmiany',
        saveDraft: 'Zapisz szkic',
      },
      activeNotice: {
        description:
          'Zmiany obejmą odpowiedzi udzielone po zapisie. Odpowiedzi już udzielone zostają przy pytaniach, na które odpowiadano.',
        title: 'Ankieta jest w użyciu',
      },
      areas: {
        description: 'Kategorie pytań w ankiecie. Wynikają z wybranych pytań.',
        empty: 'Obszary pojawią się po dodaniu pytań.',
        questions: {
          count_few: '{{count}} pytania',
          count_many: '{{count}} pytań',
          count_one: '{{count}} pytanie',
        },
        shareLabel: 'Obszar {{name}}: {{count}} z {{total}}',
        title: 'Obszary',
      },
      backToList: 'Wróć do listy ankiet',
      basics: {
        anonymous: 'Odpowiedzi anonimowe',
        anonymousHelper: 'W raportach odpowiedzi nie są przypisane do osoby.',
        anonymousLocked: 'Ankieta była już w użyciu, więc tego ustawienia nie można zmienić.',
        description: 'Nazwa ankiety, instrukcja dla uczestnika i sposób zapisu odpowiedzi.',
        instructions: 'Instrukcja',
        instructionsHelper:
          'Uczestnik zobaczy ją przed rozpoczęciem ankiety. Pole można zostawić puste.',
        name: 'Nazwa',
        namePlaceholder: 'Np. Ocena szkolenia wstępnego',
        title: 'Podstawy',
      },
      create: {
        description:
          'Nowa ankieta zapisze się jako szkic. Uczestnicy jej nie zobaczą, dopóki nie zostanie aktywowana.',
        title: 'Nowa ankieta',
      },
      edit: {
        description: 'Zmieniono {{date}}, {{person}}',
        notFound: {
          description: 'Link mógł stracić ważność. Wróć do listy ankiet i otwórz ankietę z listy.',
          title: 'Nie znaleziono ankiety',
        },
      },
      errors: {
        anonymityLocked:
          'Ankieta była już w użyciu, więc nie można zmienić anonimowości odpowiedzi.',
        conflict: 'Nie udało się zapisać zmian. Odśwież stronę i spróbuj ponownie.',
        forbidden: 'Nie masz uprawnień do zapisania tej ankiety.',
        gaps: 'Nie można zapisać zmian. Ankieta jest w użyciu i po tej zmianie nie byłaby gotowa do wypełnienia.',
        nameTaken: 'Ankieta o tej nazwie już istnieje. Wpisz inną nazwę.',
        notFound: 'Ta ankieta już nie istnieje. Wróć do listy ankiet.',
      },
      gaps: {
        description: 'Co trzeba poprawić, zanim ankieta będzie gotowa do wypełnienia.',
        noGaps: 'Ankieta nie ma braków.',
        readyDraft: 'Ankieta jest gotowa do aktywacji.',
        savedOnly: 'Lista dotyczy zapisanej ankiety. Zapisz zmiany, aby ją odświeżyć.',
        title: 'Braki',
      },
      notifications: {
        created: 'Zapisano szkic ankiety.',
        saved: 'Zapisano zmiany.',
        savedActive: 'Zapisano zmiany. Obejmą odpowiedzi udzielone od teraz.',
      },
      questions: {
        description:
          'Ankieta zadaje pytania w tej kolejności. Przeciągnij pytanie, aby zmienić jego miejsce.',
        empty: {
          description:
            'Wyszukaj pytanie ankietowe i dodaj je do listy. Szkic można zapisać bez pytań.',
          title: 'Ankieta nie ma jeszcze pytań',
        },
        limit: 'Ankieta ma już najwięcej pytań, jakie może mieć: {{max}}.',
        listLabel: 'Pytania ankiety',
        moveDown: 'Przesuń pytanie {{key}} niżej',
        moveUp: 'Przesuń pytanie {{key}} wyżej',
        needsAttention: 'Do poprawy',
        noResults: 'Brak pasujących pytań ankietowych.',
        noSummary: 'Brak treści',
        remove: 'Usuń pytanie {{key}}',
        search: 'Dodaj pytanie',
        searchFailed: 'Nie udało się wyszukać pytań. Spróbuj ponownie.',
        searchHint: 'Wpisz co najmniej 3 znaki.',
        searching: 'Szukanie pytań',
        searchPlaceholder: 'Szukaj po treści pytania',
        title: 'Pytania',
        typeBlocked: 'Tylko jednokrotny wybór albo pytanie otwarte',
      },
      questionSetDialog: {
        cancel: 'Anuluj',
        confirm: 'Zapisz zmiany',
        description:
          'Odpowiedzi udzielone przed zmianą i po niej będzie trudno porównać. Odpowiedzi już udzielone zostają przy dawnych pytaniach.',
        title: 'Zmienić pytania ankiety, która była już w użyciu?',
      },
      serverErrors: {
        anonymous: 'Ankieta była już w użyciu, więc nie można zmienić anonimowości odpowiedzi.',
        instructions: 'Skróć instrukcję do 2000 znaków.',
        name: 'Sprawdź nazwę ankiety.',
        questions:
          'Sprawdź pytania. Każde może wystąpić raz i musi być pytaniem ankietowym jednokrotnego wyboru albo otwartym.',
      },
    },
    gaps: {
      [SurveyGapCodeDto.NoQuestions]: 'Ankieta nie ma pytań. Dodaj co najmniej jedno.',
      [SurveyGapCodeDto.QuestionNotInUse]: 'Pytanie {{key}} nie ma aktywnej wersji.',
      [SurveyGapCodeDto.TranslationNotApproved]: 'Pytanie {{key}} nie ma zatwierdzonej treści.',
      unknownQuestion: 'z listy',
    },
    history: {
      backToSurvey: 'Wróć do ankiety',
      description: 'Kto i co zmienił w ankiecie. Historii nie można edytować.',
      fields: {
        anonymous: 'Odpowiedzi anonimowe',
        copiedFrom: 'Kopia ankiety',
        instruction: 'Instrukcja',
        name: 'Nazwa',
        questions: 'Pytania',
        status: 'Status',
      },
      notFound: {
        action: 'Wróć do listy ankiet',
        description: 'Link mógł stracić ważność. Wróć do listy ankiet i otwórz ankietę z listy.',
        title: 'Nie znaleziono ankiety',
      },
      open: 'Historia zmian',
    },
    listWarning: {
      label: 'Wymaga uwagi',
      tooltip:
        'Baza pytań zmieniła się od aktywacji ankiety i części pytań nie da się teraz zadać. Otwórz ankietę i sprawdź braki.',
    },
    preview: {
      close: 'Zamknij',
      description:
        'Tak ankietę zobaczy uczestnik. Wybierz stanowisko, aby zobaczyć pytania przeznaczone dla niego.',
      empty: {
        description: 'Żadne pytanie ankiety nie jest przeznaczone dla wybranego stanowiska.',
        title: 'Brak pytań dla tego stanowiska',
      },
      error: {
        description: 'Spróbuj ponownie za chwilę.',
        title: 'Nie udało się pokazać podglądu',
      },
      notFound: {
        description: 'Ankieta mogła zostać usunięta. Zamknij okno i odśwież listę.',
        title: 'Nie znaleziono ankiety',
      },
      open: 'Podgląd',
      position: 'Stanowisko uczestnika',
      positionPlaceholder: 'Wszystkie stanowiska',
      questionNumber: 'Pytanie {{number}} z {{total}}',
      refused: 'Nie można pokazać podglądu',
      saveFirst: 'Najpierw zapisz zmiany. Podgląd pokazuje zapisaną ankietę.',
      title: 'Podgląd ankiety: {{name}}',
    },
    responses: {
      backToSurveys: 'Wróć do listy ankiet',
      columns: {
        endedAt: 'Zakończenie',
        person: 'Osoba',
        position: 'Stanowisko',
        startedAt: 'Rozpoczęcie',
        startedBy: 'Uruchomiono przez',
        state: 'Status',
      },
      details: {
        anonymity: { anonymous: 'Anonimowa', named: 'Imienna' },
        answers: {
          anonymous: 'Ankieta jest anonimowa, więc odpowiedzi jednej osoby nie są pokazywane.',
          answeredAt: 'Odpowiedź {{time}}',
          emptyInProgress: 'Osoba nie skończyła jeszcze wypełniać ankiety.',
          emptyRefused:
            'Ankieta została odrzucona albo przerwana, więc odpowiedzi nie zostały zapisane.',
          noAnswer: 'Bez odpowiedzi',
          questionNumber: 'Pytanie {{number}}',
          title: 'Odpowiedzi',
        },
        backToList: 'Wróć do wypełnień',
        fields: {
          anonymity: 'Ankieta',
          endedAt: 'Zakończenie',
          position: 'Stanowisko',
          sapNumber: 'Numer SAP',
          startedAt: 'Rozpoczęcie',
          startedBy: 'Uruchomiono przez',
        },
        forbidden: {
          action: 'Wróć do wypełnień',
          description: 'Nie masz dostępu do tego wypełnienia.',
          title: 'Wypełnienie niedostępne',
        },
        notFound: {
          action: 'Wróć do wypełnień',
          description: 'Wypełnienie mogło zostać usunięte albo link jest błędny.',
          title: 'Nie znaleziono wypełnienia',
        },
        notYet: 'Jeszcze nie',
        summaryLabel: 'Podsumowanie',
      },
      empty: {
        description: 'Uruchom ankietę z listy ankiet, a wypełnienie pojawi się tutaj.',
        title: 'Nikt jeszcze nie wypełnił tej ankiety',
      },
      filters: {
        allStatuses: 'Wszystkie statusy',
        sapNumber: 'Numer SAP',
        sapNumberPlaceholder: 'Wpisz numer SAP',
        startedFrom: 'Rozpoczęte od',
        startedTo: 'Rozpoczęte do',
        status: 'Status',
        statuses: {
          [SurveyResponseStatusDto.InProgress]: 'W trakcie',
          [SurveyResponseStatusDto.Refused]: 'Odrzucona lub przerwana',
          [SurveyResponseStatusDto.Submitted]: 'Wypełniona',
        },
      },
      state: {
        [SurveyResponseStateEnum.IN_PROGRESS]: 'W trakcie',
        [SurveyResponseStateEnum.INTERRUPTED]: 'Przerwana',
        [SurveyResponseStateEnum.REFUSED]: 'Odrzucona',
        [SurveyResponseStateEnum.SUBMITTED]: 'Wypełniona',
      },
      tableLabel: 'Wypełnienia ankiety',
      title: 'Wypełnienia',
    },
    search: {
      label: 'Szukaj po nazwie',
    },
    startResponse: {
      cancel: 'Anuluj',
      confirm: 'Uruchom ankietę',
      description:
        'Wpisz dane osoby, która będzie wypełniać ankietę. Ankieta otworzy się w tym oknie, bez menu aplikacji.',
      failed: 'Nie udało się uruchomić ankiety. Spróbuj ponownie.',
      gapsTitle: 'Ankieta nie jest gotowa dla tego stanowiska',
      name: 'Imię i nazwisko',
      notActive: 'Ta ankieta nie jest już aktywna, więc nie można jej uruchomić.',
      notReady:
        'Część pytań dla tego stanowiska nie jest gotowa. Popraw je albo wybierz inne stanowisko.',
      position: 'Stanowisko osoby',
      positionPlaceholder: 'Wybierz stanowisko',
      sapNumber: 'Numer SAP',
      title: 'Uruchom ankietę: {{name}}',
    },
    status: {
      [SurveyStatusDto.Active]: 'Aktywna',
      [SurveyStatusDto.Draft]: 'Szkic',
      [SurveyStatusDto.Retired]: 'Wyłączona',
    },
    statusDialog: {
      actions: {
        [StatusActionEnum.ACTIVATE]: {
          confirm: 'Aktywuj',
          description:
            'Uczestnicy będą mogli wypełnić ankietę. Potem nie będzie można zmienić anonimowości odpowiedzi.',
          done: 'Ankieta „{{name}}” jest aktywna.',
          refusedTitle: 'Nie można aktywować ankiety',
          title: 'Aktywować ankietę „{{name}}”?',
        },
        [StatusActionEnum.RESTORE]: {
          confirm: 'Przywróć',
          description: 'Ankieta znów będzie aktywna i uczestnicy będą mogli ją wypełnić.',
          done: 'Przywrócono ankietę „{{name}}”.',
          refusedTitle: 'Nie można przywrócić ankiety',
          title: 'Przywrócić ankietę „{{name}}”?',
        },
        [StatusActionEnum.RETIRE]: {
          confirm: 'Wyłącz',
          description:
            'Nikt nie wypełni już tej ankiety. Odpowiedzi zostają. Ankietę można później przywrócić.',
          done: 'Wyłączono ankietę „{{name}}”.',
          refusedTitle: 'Nie można wyłączyć ankiety',
          title: 'Wyłączyć ankietę „{{name}}”?',
        },
      },
      cancel: 'Anuluj',
      close: 'Zamknij',
      failed: 'Nie udało się zmienić statusu ankiety. Spróbuj ponownie.',
      forbidden: 'Nie masz uprawnień do zmiany statusu tej ankiety.',
      nameTaken:
        'Inna ankieta, która nie jest wyłączona, ma już tę nazwę. Zmień nazwę jednej z ankiet, a potem przywróć tę ankietę.',
      openSurvey: 'Otwórz ankietę',
      refusedIntro: 'Najpierw popraw te miejsca, potem spróbuj ponownie:',
    },
    tableLabel: 'Ankiety',
    title: 'Ankiety',
  },
  tests: {
    actions: {
      copy: 'Kopiuj',
      delete: 'Usuń',
      deleteOnlyDraft: 'Usunąć można tylko szkic',
      edit: 'Edytuj',
      history: 'Historia zmian',
      menu: 'Akcje testu {{name}}',
      runExam: 'Przeprowadź egzamin',
      runExamOnlyActive: 'Przeprowadzić można tylko aktywny egzamin',
      sample: 'Próbne losowanie',
      start: 'Uruchom test',
      startOnlyActive: 'Uruchomić można tylko aktywny test',
    },
    addTest: 'Nowy test',
    columns: {
      actions: 'Akcje',
      length: 'Pytania',
      name: 'Nazwa',
      parts: 'Części',
      purpose: 'Cel',
      status: 'Status',
      updatedAt: 'Zmieniono',
    },
    copied: 'Utworzono kopię „{{name}}”.',
    copyFailed: 'Nie udało się skopiować testu. Spróbuj ponownie.',
    deleteDialog: {
      cancel: 'Anuluj',
      confirm: 'Usuń',
      description: 'Szkic zniknie z listy testów. Tej operacji nie można cofnąć.',
      done: 'Usunięto test „{{name}}”.',
      failed: 'Nie udało się usunąć testu. Spróbuj ponownie.',
      forbidden: 'Nie masz uprawnień do usunięcia tego testu.',
      notDraft: 'Ten test był już w użyciu, więc nie można go usunąć.',
      title: 'Usunąć test „{{name}}”?',
    },
    description:
      'Testy wiedzy i egzaminy złożone z pytań z bazy. Zawęź listę filtrami albo wyszukaj po nazwie.',
    empty: {
      description: 'Utwórz pierwszy test i dodaj do niego części z pytaniami z bazy.',
      title: 'Brak testów',
    },
    filters: {
      allCategories: 'Wszystkie kategorie',
      allPurposes: 'Każdy cel',
      allStatuses: 'Każdy status',
      category: 'Kategoria',
      purpose: 'Cel',
      status: 'Status',
    },
    form: {
      actions: {
        cancel: 'Anuluj',
        save: 'Zapisz zmiany',
        saveDraft: 'Zapisz szkic',
      },
      backToList: 'Wróć do listy testów',
      basics: {
        description: 'Nazwa i cel testu oraz instrukcja dla uczestnika.',
        instructions: 'Instrukcja',
        instructionsHelper:
          'Uczestnik zobaczy ją przed rozpoczęciem testu. Pole można zostawić puste.',
        name: 'Nazwa',
        namePlaceholder: 'Np. Test etapowy: obsługa klienta',
        purpose: 'Cel',
        purposePlaceholder: 'Wybierz cel testu',
        title: 'Podstawy',
      },
      create: {
        description:
          'Nowy test zapisze się jako szkic. Uczestnicy go nie zobaczą, dopóki nie zostanie aktywowany.',
        title: 'Nowy test',
      },
      edit: {
        activeNotice: {
          description:
            'Zmiany obejmą podejścia rozpoczęte po zapisie. Podejścia już rozpoczęte zostają bez zmian.',
          title: 'Test jest w użyciu',
        },
        description: 'Zmieniono {{date}}, {{person}}',
        notFound: {
          description: 'Link mógł stracić ważność. Wróć do listy testów i otwórz test z listy.',
          title: 'Nie znaleziono testu',
        },
      },
      errors: {
        forbidden: 'Nie masz uprawnień do zapisania tego testu.',
        gaps: 'Nie można zapisać zmian. Test jest w użyciu i po tej zmianie nie byłby gotowy do podejścia.',
        nameTaken: 'Test o tej nazwie już istnieje. Wpisz inną nazwę.',
        notFound: 'Ten test już nie istnieje. Wróć do listy testów.',
      },
      notifications: {
        created: 'Zapisano szkic testu.',
        saved: 'Zapisano zmiany.',
        savedActive: 'Zapisano zmiany. Obejmą podejścia rozpoczęte od teraz.',
      },
      parts: {
        add: 'Dodaj część',
        addLimit: 'Test może mieć najwyżej {{max}} części.',
        category: 'Kategoria',
        categoryPlaceholder: 'Wybierz kategorię',
        chosen: {
          blocked: 'Tylko w egzaminie praktycznym',
          empty: 'Nie wybrano jeszcze pytań.',
          limit: 'Ta część ma już najwięcej pytań, jakie może mieć: {{max}}.',
          noResults: 'Brak pasujących pytań testowych.',
          noSummary: 'Brak treści',
          remove: 'Usuń pytanie {{key}}',
          search: 'Dodaj pytanie',
          searchFailed: 'Nie udało się wyszukać pytań. Spróbuj ponownie.',
          searchHint: 'Wpisz co najmniej 3 znaki.',
          searching: 'Szukanie pytań',
          searchPlaceholder: 'Szukaj po treści pytania',
        },
        description:
          'Test przechodzi przez części w tej kolejności. Każda część bierze pytania z kategorii albo z listy wybranych pytań.',
        empty: {
          description:
            'Dodaj część, aby wskazać, z jakich pytań składa się test. Szkic można zapisać bez części.',
          title: 'Test nie ma jeszcze części',
        },
        moveDown: 'Przesuń część {{number}} niżej',
        moveUp: 'Przesuń część {{number}} wyżej',
        questionCount: 'Liczba pytań',
        remove: 'Usuń część {{number}}',
        title: 'Części',
      },
      pool: {
        available: 'Dostępne',
        description:
          'Ile pytań może trafić do każdej części. Pula przelicza się przy każdej zmianie celu i części.',
        length: 'Długość testu',
        needed: 'Potrzeba',
        noQuestions: 'Brak pytań',
        notReady: 'Za mało pytań',
        positions: {
          description:
            'Ile pytań może dostać osoba na danym stanowisku. To informacja, nie warunek aktywacji.',
          other: 'Pozostałe stanowiska',
          part: 'Część {{number}}',
          position: 'Stanowisko',
          ready: 'Gotowość',
          title: 'Pula według stanowisk',
        },
        practicalOnlyHint:
          'Pytania oceniane przez egzaminatora albo o wartości innej niż 1 punkt pasują tylko do egzaminu praktycznego.',
        progressLabel: 'Dostępne pytania: {{available}} z {{needed}}',
        questions: {
          count_few: '{{count}} pytania',
          count_many: '{{count}} pytań',
          count_one: '{{count}} pytanie',
        },
        ready: 'Wystarczy pytań',
        shortBy: 'Brakuje {{count}}',
        skipped: {
          [PoolSkipReasonDto.ChosenInAnotherPart]: {
            count_few: '{{count}} pytania wybrane w innej części',
            count_many: '{{count}} pytań wybranych w innej części',
            count_one: '{{count}} pytanie wybrane w innej części',
          },
          [PoolSkipReasonDto.MarkedByExaminer]: {
            count_few: '{{count}} pytania oceniane przez egzaminatora',
            count_many: '{{count}} pytań ocenianych przez egzaminatora',
            count_one: '{{count}} pytanie oceniane przez egzaminatora',
          },
          [PoolSkipReasonDto.NoApprovedWording]: {
            count_few: '{{count}} pytania bez zatwierdzonej treści',
            count_many: '{{count}} pytań bez zatwierdzonej treści',
            count_one: '{{count}} pytanie bez zatwierdzonej treści',
          },
          [PoolSkipReasonDto.NotInUse]: {
            count_few: '{{count}} pytania bez aktywnej wersji',
            count_many: '{{count}} pytań bez aktywnej wersji',
            count_one: '{{count}} pytanie bez aktywnej wersji',
          },
          [PoolSkipReasonDto.NotOnePoint]: {
            count_few: '{{count}} pytania o wartości innej niż 1 punkt',
            count_many: '{{count}} pytań o wartości innej niż 1 punkt',
            count_one: '{{count}} pytanie o wartości innej niż 1 punkt',
          },
          [PoolSkipReasonDto.ScoredBySystem]: {
            count_few: '{{count}} pytania oceniane automatycznie',
            count_many: '{{count}} pytań ocenianych automatycznie',
            count_one: '{{count}} pytanie oceniane automatycznie',
          },
          [PoolSkipReasonDto.SurveyQuestion]: {
            count_few: '{{count}} pytania ankietowe',
            count_many: '{{count}} pytań ankietowych',
            count_one: '{{count}} pytanie ankietowe',
          },
        },
        skippedIntro: 'Poza pulą: {{reasons}}.',
        states: {
          error: {
            description: 'Spróbuj ponownie za chwilę.',
            title: 'Nie udało się policzyć puli',
          },
          forbidden: {
            description: 'Nie masz uprawnień do podglądu puli pytań tego testu.',
            title: 'Brak dostępu do puli',
          },
          incomplete: {
            description:
              'W każdej części wybierz kategorię i liczbę pytań albo dodaj pytania. Pula przeliczy się sama.',
            title: 'Uzupełnij części',
          },
          noParts: {
            description: 'Dodaj część, aby zobaczyć, ile pytań może do niej trafić.',
            title: 'Test nie ma jeszcze części',
          },
          noPurpose: {
            description: 'Pula zależy od celu testu. Wybierz go w sekcji Podstawy.',
            title: 'Najpierw wybierz cel testu',
          },
          rejected: {
            description: 'Zapisz test, aby zobaczyć, które pola trzeba poprawić.',
            title: 'Nie da się policzyć puli dla tych ustawień',
          },
        },
        sufficient: 'Wystarcza',
        title: 'Pula pytań',
        warnings: {
          [TestPoolWarningCodeDto.FixedSetWithReview]:
            'Uczestnik zobaczy swoje błędy po teście, a w tej części za każdym razem pojawiają się te same pytania. Podsumowanie pokaże te błędy bez poprawnych odpowiedzi.',
          [TestPoolWarningCodeDto.PracticalExamVaries]:
            'Ta część losuje mniej pytań, niż ma w puli, więc uczestnicy dostaną różne egzaminy.',
          [TestPoolWarningCodeDto.SmallPoolWithReview]:
            'Uczestnik zobaczy swoje błędy po teście, a ta część ma mniej niż dwa razy tyle pytań, ile losuje. Kolejne podejście przyniesie w większości te same pytania.',
        },
      },
      rules: {
        allowGoingBack: 'Powrót do pytań',
        allowGoingBackHelper: 'Uczestnik może wrócić do wcześniejszych pytań w trakcie testu.',
        attemptLimit: 'Limit podejść',
        attemptLimits: {
          add: 'Dodaj wyjątek',
          description: 'Stanowisko może mieć inny limit podejść niż pozostali uczestnicy.',
          limit: 'Limit podejść',
          position: 'Stanowisko',
          positionPlaceholder: 'Szukaj po nazwie albo numerze',
          remove: 'Usuń wyjątek {{number}}',
          title: 'Wyjątki od limitu podejść',
        },
        description: 'Zaliczenie, czas i podejścia.',
        lockAfterPass: 'Blokada po zaliczeniu',
        lockAfterPassHelper: 'Po zaliczeniu uczestnik nie może podejść do testu ponownie.',
        mistakeReview: 'Przegląd błędów po teście',
        noLimitHelper: 'Puste pole oznacza brak limitu.',
        passThreshold: 'Próg zaliczenia (%)',
        passThresholdHelper: 'Puste pole oznacza test bez zaliczenia, np. diagnozę.',
        retryAdvice: 'Odstęp przed kolejnym podejściem',
        retryAdviceHelper: 'Wskazówka dla uczestnika. Nie blokuje kolejnego podejścia.',
        retryAdvicePlaceholder: 'Np. 5 dni',
        timeLimit: 'Limit czasu (min)',
        title: 'Zasady',
      },
      serverErrors: {
        attemptLimit: 'Wpisz od 1 do 99 podejść albo zostaw pole puste.',
        attemptLimitPosition:
          'Wybierz aktywne stanowisko. Każde stanowisko może mieć jeden wyjątek.',
        attemptLimits: 'Sprawdź wyjątki od limitu podejść.',
        instructions: 'Skróć instrukcję do 2000 znaków.',
        mistakeReview: 'Wybierz sposób przeglądu błędów.',
        name: 'Sprawdź nazwę testu.',
        partCategory: 'Wybierz kategorię, która jest w użyciu i nie występuje w innej części.',
        partQuestionCount: 'Wpisz liczbę pytań od 1 do 200.',
        partQuestions:
          'Sprawdź wybrane pytania. Każde może wystąpić w teście raz, a pytania oceniane przez egzaminatora pasują tylko do egzaminu praktycznego.',
        parts: 'Sprawdź części testu. Test może mieć najwyżej 20 części.',
        passThreshold: 'Wpisz próg od 1 do 100 albo zostaw pole puste.',
        purpose: 'Wybierz cel testu.',
        retryAdvice: 'Skróć wskazówkę do 100 znaków.',
        timeLimitMinutes: 'Wpisz od 1 do 600 minut albo zostaw pole puste.',
      },
    },
    history: {
      backToTest: 'Wróć do testu',
      description: 'Kto i co zmienił w teście. Historii nie można edytować.',
      fields: {
        allowGoingBack: 'Powrót do pytań',
        attemptLimit: 'Limit podejść',
        attemptLimits: 'Limity podejść dla stanowisk',
        copiedFrom: 'Kopia testu',
        instruction: 'Instrukcja',
        lockAfterPass: 'Blokada po zaliczeniu',
        mistakeReview: 'Przegląd błędów po teście',
        name: 'Nazwa',
        parts: 'Części',
        passThreshold: 'Próg zaliczenia',
        purpose: 'Cel',
        retryAdvice: 'Odstęp przed kolejnym podejściem',
        status: 'Status',
        timeLimitMinutes: 'Limit czasu',
      },
      notFound: {
        action: 'Wróć do listy testów',
        description: 'Link mógł stracić ważność. Wróć do listy testów i otwórz test z listy.',
        title: 'Nie znaleziono testu',
      },
      open: 'Historia zmian',
      values: {
        minutes: '{{value}} min',
        percent: '{{value}}%',
      },
    },
    listWarning: {
      label: 'Brakuje pytań',
      tooltip:
        'Baza pytań zmieniła się od aktywacji testu i w niektórych częściach brakuje teraz pytań. Otwórz test i sprawdź pulę pytań.',
    },
    mistakeReview: {
      [MistakeReviewDto.Mistakes]: 'Pokaż błędy',
      [MistakeReviewDto.MistakesWithExplanations]: 'Pokaż błędy z wyjaśnieniami',
      [MistakeReviewDto.None]: 'Bez przeglądu',
    },
    partKind: {
      [TestPartKindDto.ChosenQuestions]: {
        description: 'Pytania wskazane z bazy.',
        label: 'Wybrane pytania',
      },
      [TestPartKindDto.Draw]: {
        description: 'Wskazana liczba pytań losowana z kategorii.',
        label: 'Losowanie z kategorii',
      },
      [TestPartKindDto.WholeCategory]: {
        description: 'Wszystkie pytania z kategorii.',
        label: 'Cała kategoria',
      },
    },
    purpose: {
      [TestPurposeDto.EntryTest]: 'Test wiedzy na start',
      [TestPurposeDto.PracticalExam]: 'Egzamin praktyczny',
      [TestPurposeDto.StageTest]: 'Test etapowy',
      [TestPurposeDto.TheoryExam]: 'Egzamin teoretyczny',
    },
    sample: {
      close: 'Zamknij',
      description:
        'Tak test może zobaczyć uczestnik. Każde losowanie daje nowy zestaw. Nic się nie zapisuje.',
      emptyPart: 'Ta część nie daje pytań w tym losowaniu.',
      error: {
        description: 'Spróbuj ponownie za chwilę.',
        title: 'Nie udało się wylosować testu',
      },
      notFound: {
        description: 'Test mógł zostać usunięty. Zamknij okno i odśwież listę.',
        title: 'Nie znaleziono testu',
      },
      open: 'Próbne losowanie',
      part: 'Część {{number}}',
      position: 'Stanowisko uczestnika',
      positionPlaceholder: 'Wszystkie stanowiska',
      questionNumber: 'Pytanie {{number}} z {{total}}',
      redraw: 'Losuj ponownie',
      refused: 'Nie można wylosować testu',
      saveFirst: 'Najpierw zapisz zmiany. Losowanie korzysta z zapisanego testu.',
      showCorrect: 'Pokaż poprawne odpowiedzi',
      title: 'Próbne losowanie: {{name}}',
    },
    search: {
      label: 'Szukaj po nazwie',
    },
    startAttempt: {
      alreadyPassed: 'Ta osoba zaliczyła już ten test.',
      blocked: {
        back: 'Wróć',
        limit: {
          confirm: 'Przyznaj podejście i uruchom test',
          confirmExam: 'Przyznaj podejście i przeprowadź egzamin',
          description:
            'Ta osoba wykorzystała wszystkie podejścia do tego testu. Możesz przyznać dodatkowe podejście i od razu uruchomić test.',
          descriptionExam:
            'Ta osoba wykorzystała wszystkie podejścia do tego egzaminu. Możesz przyznać dodatkowe podejście i od razu przeprowadzić egzamin.',
          failed: 'Nie udało się przyznać podejścia. Spróbuj ponownie.',
          reason: 'Powód dodatkowego podejścia',
          reasonPlaceholder: 'Opisz krótko, dlaczego.',
          title: 'Wykorzystano wszystkie podejścia',
        },
        locked: {
          confirm: 'Odblokuj i uruchom test',
          confirmExam: 'Odblokuj i przeprowadź egzamin',
          description:
            'Podejścia tej osoby do tego testu są zablokowane. Możesz zdjąć blokadę i od razu uruchomić test.',
          descriptionExam:
            'Podejścia tej osoby do tego egzaminu są zablokowane. Możesz zdjąć blokadę i od razu przeprowadzić egzamin.',
          failed: 'Nie udało się odblokować podejść. Spróbuj ponownie.',
          title: 'Podejścia są zablokowane',
        },
      },
      cancel: 'Anuluj',
      confirm: 'Uruchom test',
      description:
        'Wpisz dane osoby, która będzie zdawać test. Test otworzy się w tym oknie, bez menu aplikacji.',
      exam: {
        alreadyPassed: 'Ta osoba zaliczyła już ten egzamin.',
        confirm: 'Przeprowadź egzamin',
        description:
          'Wpisz dane osoby egzaminowanej. Potem otworzy się karta egzaminu, a każda ocena zapisze się od razu.',
        failed: 'Nie udało się rozpocząć egzaminu. Spróbuj ponownie.',
        notReady:
          'Brakuje pytań, aby wylosować ten egzamin dla wybranego stanowiska. Dodaj pytania albo wybierz inne stanowisko.',
        title: 'Przeprowadź egzamin: {{name}}',
      },
      failed: 'Nie udało się uruchomić testu. Spróbuj ponownie.',
      name: 'Imię i nazwisko',
      notReady:
        'Brakuje pytań, aby wylosować ten test dla wybranego stanowiska. Dodaj pytania albo wybierz inne stanowisko.',
      position: 'Stanowisko osoby',
      positionPlaceholder: 'Wybierz stanowisko',
      sapNumber: 'Numer SAP',
      targetPosition: 'Stanowisko, na które zdaje',
      targetPositionHelper: 'Zostaw puste, jeśli to stanowisko, które osoba zajmuje.',
      targetPositionPlaceholder: 'Takie samo jak stanowisko osoby',
      title: 'Uruchom test: {{name}}',
    },
    status: {
      [TestStatusDto.Active]: 'Aktywny',
      [TestStatusDto.Draft]: 'Szkic',
      [TestStatusDto.Retired]: 'Wyłączony',
    },
    statusDialog: {
      actions: {
        [StatusActionEnum.ACTIVATE]: {
          confirm: 'Aktywuj',
          description:
            'Uczestnicy będą mogli rozpocząć test. Zmiany zapisane później obejmą tylko nowe podejścia.',
          done: 'Test „{{name}}” jest aktywny.',
          refusedTitle: 'Nie można aktywować testu',
          title: 'Aktywować test „{{name}}”?',
        },
        [StatusActionEnum.RESTORE]: {
          confirm: 'Przywróć',
          description: 'Test znów będzie aktywny i uczestnicy będą mogli go rozpocząć.',
          done: 'Przywrócono test „{{name}}”.',
          refusedTitle: 'Nie można przywrócić testu',
          title: 'Przywrócić test „{{name}}”?',
        },
        [StatusActionEnum.RETIRE]: {
          confirm: 'Wyłącz',
          description:
            'Nikt nie rozpocznie już tego testu. Podejścia i wyniki zostają. Test można później przywrócić.',
          done: 'Wyłączono test „{{name}}”.',
          refusedTitle: 'Nie można wyłączyć testu',
          title: 'Wyłączyć test „{{name}}”?',
        },
      },
      cancel: 'Anuluj',
      categoryNotInUseAnyPart:
        'Jedna z części losuje pytania z wyłączonej kategorii. Wybierz dla niej inną kategorię.',
      close: 'Zamknij',
      failed: 'Nie udało się zmienić statusu testu. Spróbuj ponownie.',
      forbidden: 'Nie masz uprawnień do zmiany statusu tego testu.',
      gaps: {
        [TestGapCodeDto.CategoryNotInUse]:
          'Część {{number}} losuje pytania z wyłączonej kategorii. Wybierz dla niej inną kategorię.',
        [TestGapCodeDto.InstructionMissing]: 'Brakuje instrukcji.',
        [TestGapCodeDto.NoParts]: 'Test nie ma części. Dodaj co najmniej jedną.',
        [TestGapCodeDto.PoolTooSmall]: 'Część {{number}} ma za mało pytań w puli.',
      },
      nameTaken:
        'Inny test, który nie jest wyłączony, ma już tę nazwę. Zmień nazwę jednego z testów, a potem przywróć ten test.',
      openTest: 'Otwórz test',
      poolTooSmallAnyPart: 'Jedna z części ma za mało pytań w puli.',
      refusedIntro: 'Najpierw popraw te miejsca, potem spróbuj ponownie:',
    },
    tableLabel: 'Testy',
    title: 'Testy',
  },
};
